import type { ComponentChildren } from "preact"
import { useState, useEffect, useMemo, useRef } from "preact/hooks"
import { Download, TrendingUp, Save, CheckCircle2, Trash2, Wand2, Crosshair, XCircle, Sparkles, Plus, Truck, Scale, Undo2, Redo2 } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import { SalesForecastingTable } from "./components/sales_forecasting_table"
import { CreateSalesSourceDialog, CreateSalesSourceFormData } from "./components/create_sales_source_dialog"
import { VersionManagerComponent } from "./components/version_manager"
import { GrossToNetPanel } from "./components/gross_to_net_panel"
import { apply_trade_rate_to_customer, build_gross_to_net, read_trade_spend, type GrossToNetBridge } from "./utils/gross_to_net"
import { live_customer_revenue } from "./utils/live_customer_revenue"
import { TradeLiftPanel } from "./components/trade_lift_panel"
import { BudgetVsActualPanel } from "./components/budget_vs_actual_panel"
import { ForecastImportDialog } from "./components/forecast_import_dialog"
import { SeedFromActualsDialog } from "./components/seed_from_actuals_dialog"
import { build_trade_lift, type TradeLiftSummary } from "./utils/trade_lift"
import { EnhancedPromotionEditModal } from "../trade_promotion_management/promo_calendar/components/enhanced_promotion_edit_modal"
import type { Promotion } from "../trade_promotion_management/promo_creation/types"
import { apply_case_cost_fill, plan_case_cost_fill } from "./utils/catalog_case_costs"
import { compare_period_year_shift } from "./utils/version_compare"
import { flatten_compare_leaves, type CompareLeaf } from "./utils/version_compare_table"
import { VersionCompareView } from "./components/version_compare_view"
import { fill_derived_units_in_hierarchy } from "./utils/units_conversion"
import { apply_catalog_overlay, packs_view_of_hierarchy } from "./utils/catalog_overlay"
import { actuals_preview_message } from "./utils/actuals_preview_message"
import { getCatalogOverlay } from "./api/forecast_catalog_api"
import { TradeSpendPanel } from "./components/trade_spend_panel"
import { searchProducts } from "../../api/product_catalog"
import { resolveRemotePromotions } from "./api/forecast_api"
import useSWR, { mutate as mutate_swr } from "swr"
import { api_fetch } from "../../api/client"
import { ChatPanel } from "../../components/forecast_chat/ChatPanel"
import { ForecastChangelogDialog } from "./components/forecast_changelog_dialog"
import { ForecastSaveConfirmDialog } from "./components/forecast_save_confirm_dialog"
import { DiscardUnsavedChangesDialog } from "./components/discard_unsaved_changes_dialog"
import { 
  SalesHierarchyNode, 
  TimePeriod, 
  ViewMode,
  HierarchyLevel,
  ForecastConfig,
  ForecastVersion,
  VersionManager,
  ColumnConfig,
  TableRow,
} from "./types"
import * as ForecastAPI from "./api/forecast_api"
import { aggregateChildrenToParents, updateDataInHierarchy, generateForecasts, updateForecastConfigInHierarchy, regenerateForecastsForRow, updateCaseCostInHierarchy, recalculateVolumeForAllRows, updateAggregatedProductLineData, updateAggregatedSKUData, aggregateDuplicateClassOfTrade, collectAllHierarchyNodeIds, removeHierarchySubtreeById, updateNodeNameInHierarchy, type EditableMetricField } from "./utils/aggregation"
import {
  addCatalogSkuUnderRow,
  addHierarchyRowsUnderContext,
  default_case_cost_from_hierarchy,
  find_product_line_parent_id,
  inferForecastLeafLevel,
} from "./utils/hierarchy_add_row"
import { AddSkuDialog } from "./components/add_sku_dialog"
import { EditLineDialog, type EditLineTarget } from "./components/edit_line_dialog"
import { node_path_by_id, rebuild_line_plan, replace_node_by_id, type LineSetup } from "./utils/line_setup"
import {
  buildJanDecPeriodsForYear,
  calendarYearFromForecastVersion,
  collect_period_keys_from_hierarchy,
  compare_period_to_forecast_start,
  default_blank_forecast_year_period,
  generatePeriods,
  parse_period_key_to_time_period,
  filter_week_column_keys,
  years_in_period_keys,
  period_keys_span_years,
  type WeekColumnWindow,
} from "./utils/periods"
import { generateSalesSourceData } from "./utils/data_generator"
import { format_cases, plan_quantity_basis, round_cases, sku_plan_reference } from "./utils/quantity_basis"
import { pick_initial_version, primary_forecast_version, read_last_opened_version_id, remember_last_opened_version_id } from "./utils/last_opened_version"
import { hierarchy_save_outcome, save_requires_approval } from "./utils/forecast_approvals"
import { can_manage_forecast_versions } from "./utils/version_access"
import { ForecastApprovalQueue, PENDING_CHANGE_REQUESTS_KEY } from "./components/forecast_approval_queue"
import { ship_to_fallback_from_customers } from "./utils/ship_to_view"
import type { CustomerKindRecord } from "./utils/customer_kind"
import { UserState } from "@/auth/user"
import { useOrgSettings } from "@/org_settings/state"
import { actuals_sync_button, last_pull_sentence } from "./utils/actuals_sync_button"
import { getIntegrationsWithJobs } from "../../api/integration"
import { normalizeHierarchyData } from "./utils/normalization"
import { buildForecastTableHierarchy, hierarchyHasActuals, summarizeForecastHierarchyTotals } from "./utils/forecast_table_pipeline"
import { build_ship_to_revenue_by_month_csv } from "./utils/ship_to_export"
import { computePlanTotalsSnapshot, type PlanTotalsSnapshot } from "./utils/plan_totals_snapshot"
import {
  effective_quantity_mode,
  is_delivered_detail_channel,
  parse_detail_metrics,
  reconcileColumnConfigWithAvailability,
  scan_forecast_measure_availability,
  type ForecastMeasureAvailability,
} from "./utils/forecast_measure_slice"
import { mergeCompareCellsIntoHierarchy } from "./utils/merge_compare"
import { evaluate_forecast_dirty_state } from "./utils/unsaved_changes"
import { ALL_REPS, filter_tree_by_rep, reps_in_records, user_rep_name, type RepCustomerRecord } from "./utils/rep_filter"
import { empty_history, record_step, redo_step, undo_key_action, undo_step, type UndoHistory } from "./utils/undo_history"
import { diff_unsaved_forecast, saved_tree_from_signature, type UnsavedDiff } from "./utils/unsaved_diff"
import { UnsavedChangesList } from "./components/unsaved_changes_list"
import {
  clear_forecast_draft,
  draft_saved_caption,
  offer_forecast_draft,
  read_forecast_draft,
  write_forecast_draft,
  type DraftOffer,
} from "./utils/forecast_draft"
import {
  extractRawSalesMetricsForTransform,
  lock_flag_for_merged_row,
  mergeForecastActualRowsForSamePeriod,
  provenance_field_for_merged_row,
  use_forecast_as_primary_for_paired_cell,
} from "./utils/merge_period_sales_data"
import {
  build_opportunity_kpi_detail_lines,
  summarize_selected_opportunity_forecast_totals,
  type OpportunityKpiDetailLine,
} from "./utils/opportunity_kpi_contribution"
import {
  OpportunityScenarioView,
  ForecastOpportunitySummary,
  buildOpportunityPrefillFromHierarchyRow,
  buildSalesSourcePrefillFromHierarchyRow,
  collectExpandedAncestorIdsForAllOpenOpportunities,
  collectExpandedAncestorIdsForOpportunityId,
  collectClosedForecastOpportunities,
  collectForecastOpportunities,
  markOpportunityLost,
  promoteOpportunityToLive,
  cleanupPromotedOpportunityPlaceholders,
  removeOpportunity,
  reopenOpportunity,
  spliceOpportunityBranchesIntoExistingCustomers,
  updateOpportunityLikelihood,
} from "./utils/opportunities"
import { exactCurrencyFormatter } from "../../utils"
import { mergeTopDownPreviewIntoHierarchy, parse_top_down_target_input } from "./utils/apply_top_down_preview"
import { toast } from "../../components/ui/use-toast"

/** A hierarchy-replacing flow held back until the user confirms discarding unsaved edits. */
interface PendingUnsavedAction {
  title: string
  description: string
  confirm_label: string
  run: () => void | Promise<void>
  /** Invoked when the user cancels instead — lets promise-based callers resolve `false`. */
  on_cancel?: () => void
}

export function SalesForecasting() {
  const className = undefined
  const [data, setData] = useState<SalesHierarchyNode[]>([])
  const [periods, setPeriods] = useState<TimePeriod[]>([])
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.CHAIN_RETAILER_VIEW)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [forecastConfig, setForecastConfig] = useState<ForecastConfig>({
    same_store_sales_multiplier: 0.03, // 3% growth
    store_count_multiplier: 0.02,      // 2% growth
    forecast_start_period: `${new Date().getFullYear()}-01`,
    enabled: true
  })
  const [hasData, setHasData] = useState(false)
  const [compareMode, setCompareMode] = useState(false)
  const [promo_lift_compare, setPromoLiftCompare] = useState(false)
  const [comparisonPeriods, setComparisonPeriods] = useState<TimePeriod[]>([])
  
  // Column configuration state
  const [columnConfig, setColumnConfig] = useState<ColumnConfig>({
    show_stores: true,
    show_cases: true,
    show_volume: true,
    metric_mode: "dollars",
    quantity_mode: "cases",
  })
  
  // Version management state
  const [versionManager, setVersionManager] = useState<VersionManager>({
    versions: [],
    current_version_id: null,
    primary_version_id: null
  })
  const [isVersionManagerOpen, setIsVersionManagerOpen] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  /** A local draft of this version found on load that differs from the server's copy. */
  const [draftOffer, setDraftOffer] = useState<DraftOffer | null>(null)
  /** When the grid's unsaved edits were last written to the browser (ISO), for the caption by Save. */
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [isCreatingSalesSource, setIsCreatingSalesSource] = useState(false)
  const [isUpdatingForecasts, setIsUpdatingForecasts] = useState(false)
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [forecastSummary, setForecastSummary] = useState<ForecastAPI.ForecastSummaryResponse | null>(null)
  const [compareCells, setCompareCells] = useState<ForecastAPI.CompareCell[]>([])
  const [compareCellsArePreview, setCompareCellsArePreview] = useState(false)
  const [isPreviewingQuickBooks, setIsPreviewingQuickBooks] = useState(false)
  const [isSavingActuals, setIsSavingActuals] = useState(false)
  const [actualizeError, setActualizeError] = useState<string | null>(null)
  const [topDownTarget, setTopDownTarget] = useState("")
  const [pendingTopDownCommit, setPendingTopDownCommit] = useState<ForecastAPI.TopDownTargetRequest | null>(null)
  const [opportunityScenarioView, setOpportunityScenarioView] = useState<OpportunityScenarioView>("weighted")
  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState<Set<string>>(new Set())
  const [showOpportunitiesPanel, setShowOpportunitiesPanel] = useState(false)
  const [show_trade_spend, set_show_trade_spend] = useState(false)
  const [quickCreatePrefill, setQuickCreatePrefill] = useState<Partial<CreateSalesSourceFormData> | null>(null)
  const [quickCreateOpenSignal, setQuickCreateOpenSignal] = useState<number | null>(null)
  /** Which customer form the New menu opens the create dialog on. */
  const [quickCreateCustomerKind, setQuickCreateCustomerKind] = useState<"consumption" | "ship_to">("consumption")
  /** Open when "New → Item (SKU)" needs the user to say which product line it goes under. */
  const [skuTargetPickerOpen, setSkuTargetPickerOpen] = useState(false)
  const [updateForecastDialogOpen, setUpdateForecastDialogOpen] = useState(false)
  /** Trade lift mode: shows the promoted-volume plan behind the forecast. */
  const [showTradeLift, setShowTradeLift] = useState(false)
  // Data in/out (forecast_io): the import and seed dialogs, and the budget-vs-actual panel.
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [showSeedDialog, setShowSeedDialog] = useState(false)
  const [showBudgetVsActual, setShowBudgetVsActual] = useState(false)
  const [budgetVsActualVersionId, setBudgetVsActualVersionId] = useState<string | null>(null)
  /**
   * Ship-week view (week grain only): column headers show the ship week (consumption week −
   * lead time). Display-only. Lead time persists per browser.
   */
  const [shipWeekView, setShipWeekView] = useState(false)
  const [shipWeekLeadTime, setShipWeekLeadTime] = useState<number>(() => {
    // 0 is a legal saved value (ship week = consumption week), so only a missing or
    // malformed entry falls back to the 3-week default — `Number(null)` is 0, which a
    // plain truthiness check would both misread and then discard.
    const raw = localStorage.getItem("forecast_ship_week_lead_time")
    if (raw == null) return 3
    const stored = Number(raw)
    return Number.isFinite(stored) && stored >= 0 && stored <= 12 ? stored : 3
  })
  /** Every period key on the loaded version; fill-forward walks this even when columns are clipped. */
  const [allPeriodKeys, setAllPeriodKeys] = useState<string[]>([])
  /** Week-grain column window. Rolling = current ISO week through the last week in the version. */
  const [weekColumnWindow, setWeekColumnWindow] = useState<WeekColumnWindow>({ kind: "rolling" })
  /** `remote_promo_id` → promotion id, for trade-calendar events that exist in the promo calendar. */
  const [promotionIdsByRemoteId, setPromotionIdsByRemoteId] = useState<Record<string, string>>({})
  /** The promotion open in the edit modal, opened from a trade-lift row. */
  const [openPromotion, setOpenPromotion] = useState<Promotion | null>(null)
  const [salesSourceDialogOpen, setSalesSourceDialogOpen] = useState(false)
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false)
  const [pendingSaveVersionId, setPendingSaveVersionId] = useState<string | null>(null)
  const [saveDialogBaseline, setSaveDialogBaseline] = useState<PlanTotalsSnapshot | null>(null)
  // "Review changes": the unsaved edits listed cell by cell, before anything is saved.
  const [showUnsavedReview, setShowUnsavedReview] = useState(false)
  const [isSavingHierarchy, setIsSavingHierarchy] = useState(false)
  const [pendingUnsavedAction, setPendingUnsavedAction] = useState<PendingUnsavedAction | null>(null)
  const pendingUnsavedActionConsumedRef = useRef(false)
  /** Customers picked from the accounting list in the create dialog; mapped to their node on the next save. */
  const pendingCustomerAliasesRef = useRef<string[]>([])
  const [isFillingCaseCosts, setIsFillingCaseCosts] = useState(false)
  const [addSkuTarget, setAddSkuTarget] = useState<import("./components/add_sku_dialog").AddSkuDialogTarget | null>(null)
  const [editLineTarget, setEditLineTarget] = useState<EditLineTarget | null>(null)
  /** Version-vs-version compare, shown as its own table in place of the grid (null = off). */
  const [versionCompare, setVersionCompare] = useState<{ id: string; name: string; year_shift: number } | null>(null)
  /** The compared version's leaves, already shifted onto this version's columns. */
  const [versionCompareLeaves, setVersionCompareLeaves] = useState<CompareLeaf[] | null>(null)
  const lastSavedPlanTotalsRef = useRef<PlanTotalsSnapshot | null>(null)
  const lastSavedDataSignatureRef = useRef("")
  // Undo / redo over the grid's unsaved edits. A step is the previous tree root; loading or saving
  // a version starts a fresh history (`resetUndoHistory`), and a step taken by undo/redo itself is
  // not recorded as a new edit.
  const undoHistoryRef = useRef<UndoHistory<SalesHierarchyNode[]>>(empty_history())
  const undoPreviousDataRef = useRef<SalesHierarchyNode[] | null>(null)
  const applyingUndoRef = useRef(false)
  const [undoCounts, setUndoCounts] = useState<{ past: number; future: number }>({ past: 0, future: 0 })

  function syncUndoCounts(): void {
    setUndoCounts({ past: undoHistoryRef.current.past.length, future: undoHistoryRef.current.future.length })
  }

  function resetUndoHistory(baseline: SalesHierarchyNode[]): void {
    undoHistoryRef.current = empty_history()
    undoPreviousDataRef.current = baseline
    syncUndoCounts()
  }

  useEffect(() => {
    setPendingTopDownCommit(null)
  }, [currentVersionId])

  function forecastDataSignature(forecastData: SalesHierarchyNode[]): string {
    return JSON.stringify(forecastData)
  }

  /**
   * Run `action` immediately when the grid is clean, otherwise confirm first. Every flow that
   * reloads the hierarchy from the server (version switch, actualize, changelog restore, AI edits)
   * overwrites in-progress cell edits, so it has to go through here.
   */
  function guardUnsavedChanges(action: PendingUnsavedAction): void {
    if (!hasUnsavedChanges) {
      void action.run()
      return
    }
    pendingUnsavedActionConsumedRef.current = false
    setPendingUnsavedAction(action)
  }

  function confirmPendingUnsavedAction(): void {
    const action = pendingUnsavedAction
    pendingUnsavedActionConsumedRef.current = true
    setPendingUnsavedAction(null)
    // Discarding the edits on purpose: do not offer them back as a draft either.
    if (currentVersionId) clear_forecast_draft(currentVersionId)
    setDraftSavedAt(null)
    void action?.run()
  }

  // Confirming also closes the dialog, so the close handler needs to know whether the
  // action already ran — otherwise `on_cancel` would fire right after a confirm.
  function cancelPendingUnsavedAction(): void {
    const action = pendingUnsavedAction
    setPendingUnsavedAction(null)
    if (action && !pendingUnsavedActionConsumedRef.current) {
      action.on_cancel?.()
    }
  }

  /**
   * Promise form of `guardUnsavedChanges` for flows that must confirm BEFORE a server
   * mutation happens (e.g. changelog revert — confirming after the restore leaves the
   * grid showing stale edits over already-reverted server state).
   */
  function confirm_discard_unsaved_edits(description: string, confirm_label: string): Promise<boolean> {
    return new Promise((resolve) => {
      guardUnsavedChanges({
        title: "Discard unsaved forecast edits?",
        description,
        confirm_label,
        run: () => resolve(true),
        on_cancel: () => resolve(false),
      })
    })
  }

  // Fetch forecast vs actual cells from the server when Compare mode is on.
  // Preview pulls set compare cells locally and skip this fetch until cleared.
  useEffect(() => {
    if (!compareMode || !currentVersionId || compareCellsArePreview) {
      return
    }
    let cancelled = false
    ForecastAPI.getForecastCompare(currentVersionId)
      .then((res) => {
        if (!cancelled) setCompareCells(res.cells)
      })
      .catch((err) => {
        console.error("Failed to fetch forecast compare", err)
        if (!cancelled) setCompareCells([])
      })
    return () => {
      cancelled = true
    }
  }, [compareMode, currentVersionId, compareCellsArePreview])

  // Which accounting system feeds actuals (QuickBooks lines parsed from each invoice, NetSuite
  // lines pulled by the daily sync). Decides the Sync actuals button's label and whether it shows.
  const { data: integrations } = useSWR("forecast-integrations", async () => {
    const res = await getIntegrationsWithJobs()
    return res.ok ? res.value.data : undefined
  }, { revalidateOnFocus: false })
  const sync_button = actuals_sync_button(integrations)
  const provider_label = sync_button.provider_label || "QuickBooks"

  /** Load accounting actuals into Compare mode only (nothing written until Save to forecast). */
  async function handleSyncActualsFromQuickBooks() {
    if (!currentVersionId || isPreviewingQuickBooks) return
    setIsPreviewingQuickBooks(true)
    setActualizeError(null)
    setPendingTopDownCommit(null)
    try {
      const res = await ForecastAPI.previewActualizeForecastVersion(currentVersionId)
      const message = actuals_preview_message(res, provider_label)
      // With nothing landed there is nothing to compare or save, so the view stays as it was.
      if (message.has_actuals) {
        setCompareCells(res.cells)
        setCompareCellsArePreview(true)
        setCompareMode(true)
      }
      toast({
        title: message.title,
        description: [message.description, last_pull_sentence(sync_button)].filter(Boolean).join(" "),
      })
    } catch (err: any) {
      const message = err?.message ?? `Failed to sync actuals from ${provider_label}`
      setActualizeError(message)
      toast({ title: "Sync failed", description: message, variant: "destructive" })
    } finally {
      setIsPreviewingQuickBooks(false)
    }
  }

  /** Persist accounting-derived actuals and reload hierarchy; compare cells refresh via useEffect. */
  function handleSaveActualsToForecast() {
    guardUnsavedChanges({
      title: "Discard unsaved forecast edits?",
      description:
        "Saving actuals writes them to this forecast and reloads the grid from the server, so the cell edits you have not saved will be lost.",
      confirm_label: "Discard and save actuals",
      run: () => performSaveActualsToForecast(),
    })
  }

  /** An import or seed wrote rows server-side: reload the grid so the saved state is what the grid shows. */
  async function reloadAfterServerWrite(): Promise<void> {
    if (!currentVersionId) return
    await loadVersionData(currentVersionId)
  }

  async function performSaveActualsToForecast() {
    if (!currentVersionId || isSavingActuals) return
    setIsSavingActuals(true)
    setActualizeError(null)
    try {
      const res = await ForecastAPI.actualizeForecastVersion(currentVersionId)
      await loadVersionData(currentVersionId)
      const skipped =
        (res.skips?.unmapped_product_lines ?? 0) +
        (res.skips?.unmapped_customer_lines ?? 0) +
        (res.skips?.no_sku_node_lines ?? 0)
      toast({
        title: "Actuals saved",
        description: [
          `Wrote ${res.points_written} ${provider_label} actual row${res.points_written === 1 ? "" : "s"} to this forecast.`,
          skipped > 0
            ? `${skipped.toLocaleString()} invoice line${skipped === 1 ? "" : "s"} could not be placed (${[
                res.skips.unmapped_product_lines ? `${res.skips.unmapped_product_lines} unmapped product` : null,
                res.skips.unmapped_customer_lines ? `${res.skips.unmapped_customer_lines} unmapped customer` : null,
                res.skips.no_sku_node_lines ? `${res.skips.no_sku_node_lines} no matching forecast SKU row` : null,
              ]
                .filter(Boolean)
                .join(", ")}) — fix under Manage mappings.`
            : null,
          res.skips.unmapped_customers?.length
            ? `Unmapped customers: ${res.skips.unmapped_customers
                .map((customer: ForecastAPI.UnmappedCustomerCount) => `${customer.name} (${customer.lines})`)
                .join(", ")}.`
            : null,
        ]
          .filter(Boolean)
          .join(" "),
      })
    } catch (err: any) {
      const message = err?.message ?? "Failed to save actuals"
      setActualizeError(message)
      toast({ title: "Could not save actuals", description: message, variant: "destructive" })
    } finally {
      setIsSavingActuals(false)
    }
  }

  const current_user = UserState.use()
  /** Forecast governance (admin-only versions, approval queue) is per org; off unless switched on. */
  const forecast_approvals_required = useOrgSettings().forecast_approvals_required

  // The brand's account list, for the Ship-to view: a customer node with no `metadata.rtm` split
  // is grouped under the distributor its record names (parent account) instead of "No
  // distributor mapping".
  const { data: customer_records } = useSWR("forecast-customer-records", async () => {
    const res = await api_fetch<CustomerKindRecord[]>("/customers", { use_cache: true })
    return res.ok ? res.value.data : []
  })
  const ship_to_fallback = useMemo(() => ship_to_fallback_from_customers(customer_records ?? []), [customer_records])

  // Rep filter: the customers whose record names this rep (`customer.sales_rep`). Display only —
  // edits and saves work on the whole tree; the server refuses a rep's change to another rep's
  // customer in a governed org (`forecast::rep_edit_block_reason`).
  const [repFilter, setRepFilter] = useState<string>(ALL_REPS)
  const rep_records = (customer_records ?? []) as unknown as RepCustomerRecord[]
  const rep_names = useMemo(() => reps_in_records(rep_records), [customer_records])
  const my_rep_name = user_rep_name(current_user as any)
  const rep_filtered_data = useMemo(
    () => filter_tree_by_rep(data, repFilter, rep_records),
    [data, repFilter, customer_records]
  )

  // Process data based on view mode. Open opportunities join the grid (at the scenario's
  // weight) only while the panel is toggled on — the default view is the committed plan.
  const processedData = useMemo(() => {
    const built = buildForecastTableHierarchy(rep_filtered_data, viewMode, opportunityScenarioView, selectedOpportunityIds, {
      include_open_opportunities: showOpportunitiesPanel,
      ship_to_fallback,
    })
    // Version compare has its own view; the actuals compare stands down while it is open.
    if (compareMode && compareCells.length > 0 && !versionCompareLeaves) {
      return aggregateChildrenToParents(mergeCompareCellsIntoHierarchy(built, compareCells))
    }
    return built
  }, [rep_filtered_data, viewMode, opportunityScenarioView, selectedOpportunityIds, showOpportunitiesPanel, compareMode, compareCells, versionCompareLeaves, ship_to_fallback])

  // NetSuite item names, pack sizes and customer sources for this version's rows.
  const { data: catalog_overlay } = useSWR(
    currentVersionId ? ["forecast-catalog-overlay", currentVersionId] : null,
    () => getCatalogOverlay(currentVersionId as string),
    { revalidateOnFocus: false },
  )

  /**
   * What the table renders: `processedData` with units derived on every leaf that has a pack
   * size but no unit count, so the Units toggle always has something to show. Display-only —
   * `data` (and therefore the dirty flag and saves) stays in cases.
   */
  const displayData = useMemo(() => {
    const with_units = fill_derived_units_in_hierarchy(apply_catalog_overlay(processedData, catalog_overlay))
    return columnConfig.quantity_mode === "packs" ? packs_view_of_hierarchy(with_units) : with_units
  }, [processedData, catalog_overlay, columnConfig.quantity_mode])
  /**
   * This version's side of a version comparison. Always flattened from the customer tree
   * (Chain/Retailer view), whatever view is on screen: the compared version arrives as raw
   * customer nodes, and a product-line or ship-to tree would name product lines or distributors
   * as "customers" and match nothing.
   */
  const this_version_compare_leaves = useMemo(
    () =>
      versionCompareLeaves
        ? flatten_compare_leaves(
            viewMode === ViewMode.CHAIN_RETAILER_VIEW
              ? processedData
              : buildForecastTableHierarchy(data, ViewMode.CHAIN_RETAILER_VIEW, opportunityScenarioView, selectedOpportunityIds, {
                  include_open_opportunities: showOpportunitiesPanel,
                  ship_to_fallback,
                })
          )
        : [],
    [processedData, data, viewMode, opportunityScenarioView, selectedOpportunityIds, showOpportunitiesPanel, ship_to_fallback, versionCompareLeaves]
  )

  async function startVersionCompare(version: ForecastVersion): Promise<void> {
    try {
      const response = await ForecastAPI.getForecastHierarchy(version.id)
      // A different fiscal year compares month-for-month on this version's columns —
      // otherwise every compared cell keys past the grid and reads $0.
      const current = versionManager.versions.find(v => v.id === versionManager.current_version_id)
      const year_shift = compare_period_year_shift(current?.fiscal_year, version.fiscal_year)
      setVersionCompareLeaves(flatten_compare_leaves(response.nodes, year_shift))
      setVersionCompare({ id: version.id, name: version.name, year_shift })
    } catch (err) {
      console.error("Failed to load comparison version", err)
      toast({
        title: "Could not load the comparison version",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      })
    }
  }

  function stopVersionCompare(): void {
    setVersionCompare(null)
    setVersionCompareLeaves(null)
  }

  const opportunities = useMemo(() => collectForecastOpportunities(data), [data])
  const closed_opportunities = useMemo(() => collectClosedForecastOpportunities(data), [data])

  // Forecast Mappings maps accounting invoice lines, which only QuickBooks and NetSuite produce:
  // the page and its chip mean nothing for any other brand.
  const has_mapping_provider = sync_button.visible

  // Mapping health for the toolbar chip: accounting invoice lines that can never land on the
  // forecast until they are mapped (the actualizer skips them, with counts in its toast).
  const { data: mapping_health } = useSWR(
    has_mapping_provider ? "forecast-mappings-health" : null,
    async () => {
      const res = await api_fetch<{ unmapped_descriptions: unknown[]; unmapped_customers: unknown[] }>(
        "/forecast-mappings",
        { method: "GET" }
      )
      return res.ok ? res.value.data : null
    },
    { revalidateOnFocus: false }
  )
  const unmapped_mapping_count =
    (mapping_health?.unmapped_descriptions?.length ?? 0) + (mapping_health?.unmapped_customers?.length ?? 0)

  const selected_opportunity_totals = useMemo(
    () => summarize_selected_opportunity_forecast_totals(opportunities, selectedOpportunityIds),
    [opportunities, selectedOpportunityIds]
  )

  /** Sub-lines on forecast KPI cards when the Opportunities control is expanded (respects opportunity card selection). */
  const opportunity_kpi_detail_lines = useMemo((): OpportunityKpiDetailLine[] | null => {
    if (opportunities.length === 0 || !showOpportunitiesPanel) {
      return null
    }
    return build_opportunity_kpi_detail_lines(opportunityScenarioView, selected_opportunity_totals)
  }, [opportunities.length, showOpportunitiesPanel, opportunityScenarioView, selected_opportunity_totals])

  const measure_availability = useMemo((): ForecastMeasureAvailability => {
    return scan_forecast_measure_availability(data)
  }, [data])

  const show_promo_lift_in_summary = useMemo(
    () =>
      promo_lift_compare &&
      columnConfig.quantity_mode !== "packs" &&
      effective_quantity_mode(columnConfig) === "units" &&
      !is_delivered_detail_channel(columnConfig),
    [promo_lift_compare, columnConfig],
  )

  const can_toggle_consumption_delivered = useMemo(
    () =>
      measure_availability.consumption_dollars &&
      measure_availability.delivered_dollars &&
      measure_availability.consumption_cases &&
      measure_availability.delivery_cases,
    [measure_availability],
  )

  useEffect(() => {
    if (
      is_delivered_detail_channel(columnConfig) ||
      columnConfig.quantity_mode === "packs" ||
      effective_quantity_mode(columnConfig) === "cases"
    ) {
      setPromoLiftCompare(false)
    }
  }, [columnConfig])

  /**
   * Whether Compare has anything to show. A pure plan year has no actuals, so the toggle would
   * render a view identical to Forecast. The preview flows (QuickBooks actuals, top-down) turn
   * compare mode on themselves without stored actuals, so they keep the toggle available —
   * otherwise there would be no way back to Forecast.
   */
  const can_compare = useMemo(
    () => hierarchyHasActuals(data) || compareCells.length > 0 || compareCellsArePreview || compareMode,
    [data, compareCells, compareCellsArePreview, compareMode]
  )

  // Leaving compare mode on across a version switch would strand the grid in a mode whose
  // toggle is hidden, so drop it when the newly loaded plan has nothing to compare.
  useEffect(() => {
    if (compareMode && !compareCellsArePreview && compareCells.length === 0 && !hierarchyHasActuals(data)) {
      setCompareMode(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVersionId, data])

  /** Date range of month columns in the grid (KPI totals use these months only, matching the footer). */
  const plan_column_range_caption = useMemo((): string | null => {
    if (periods.length === 0) {
      return null
    }
    return `${periods[0].period_key}–${periods[periods.length - 1].period_key}`
  }, [periods])

  /**
   * Gross-to-net bridge from trade spend on customer nodes; null unless the version carries it.
   * Revenue comes from the same scenario-adjusted tree the grid and KPI cards use, so Net Revenue
   * and the bridge move with Baseline / Weighted / Full Upside and with unsaved edits.
   */
  const gross_to_net = useMemo(() => {
    const period_keys = periods.map((p: TimePeriod) => p.period_key)
    return build_gross_to_net(data, period_keys, live_customer_revenue(data, processedData, period_keys, columnConfig))
  }, [data, processedData, periods, columnConfig])

  /**
   * Expected lift from trade spend, from the trade calendar on customer nodes. `null` unless the
   * version carries one, which is what keeps the Trade lift toggle hidden for everyone else.
   */
  const trade_lift = useMemo(
    () => build_trade_lift(data, periods.map((p: TimePeriod) => p.period_key)),
    [data, periods]
  )

  /** True when the loaded version plans by ISO week — gates the Ship-weeks toggle. */
  const is_week_grain = useMemo(() => periods.some((p: TimePeriod) => p.grain === "week"), [periods])

  /**
   * Periods a new sales source can start, end and be generated in. A weekly plan shows a window
   * of its weeks, but a source added while 2026 is on screen still runs into 2027 — the same
   * reason fill-forward walks `allPeriodKeys` rather than the visible columns.
   */
  const sales_source_periods = useMemo(
    () =>
      is_week_grain && allPeriodKeys.length > 0
        ? allPeriodKeys.map((pk: string) => parse_period_key_to_time_period(pk))
        : periods,
    [is_week_grain, allPeriodKeys, periods]
  )

  const week_column_years = useMemo(() => years_in_period_keys(allPeriodKeys), [allPeriodKeys])
  const show_week_column_window = is_week_grain && (week_column_years.length > 1 || allPeriodKeys.length > 52)

  useEffect(() => {
    if (!is_week_grain) return
    const keys = filter_week_column_keys(allPeriodKeys, weekColumnWindow, new Date())
    const visible = keys.length > 0 ? keys : allPeriodKeys
    setPeriods(visible.map((pk) => parse_period_key_to_time_period(pk)))
  }, [weekColumnWindow, allPeriodKeys, is_week_grain])

  /**
   * Match the trade-calendar events on screen to promotions in the promo calendar, so their names
   * can link. Only runs while the panel is open — it is a batch lookup nobody needs otherwise —
   * and a miss leaves the row unlinked rather than failing the panel.
   */
  const trade_lift_remote_ids_key = (trade_lift?.remote_promo_ids ?? []).join("|")
  useEffect(() => {
    if (!showTradeLift || !trade_lift || trade_lift.remote_promo_ids.length === 0) return
    let cancelled = false
    void (async () => {
      const resolved = await resolveRemotePromotions(trade_lift.remote_promo_ids)
      if (cancelled) return
      const next: Record<string, string> = {}
      for (const row of resolved) next[row.remote_promo_id] = row.promotion_id
      setPromotionIdsByRemoteId(next)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTradeLift, trade_lift_remote_ids_key])

  /** Open a promotion from a trade-lift row in the promo calendar's own edit modal. */
  async function handleOpenPromotionFromTradeLift(promotion_id: string): Promise<void> {
    const result = await api_fetch<{ id: string; promotion: Promotion }>(`/promotions/${promotion_id}`)
    if (result.ok) {
      setOpenPromotion(result.value.data.promotion)
    }
  }

  /** Every product-line row, with its full path — the targets "New → Item (SKU)" can add under. */
  const product_line_targets = useMemo(() => collect_product_line_targets(data), [data])

  /** Month labels for the lift panel's columns, so they read like the grid's headers. */
  const trade_lift_period_labels = useMemo((): Record<string, string> => {
    const labels: Record<string, string> = {}
    for (const period of periods) {
      if (period.label) labels[period.period_key] = period.label
    }
    return labels
  }, [periods])

  /**
   * Gross-to-net panel rate edit: rescale every same-named customer node's trade spend
   * to the target rate for the periods on screen. A normal unsaved edit — metadata
   * round-trips through the standard hierarchy save.
   */
  function handleTradeRateChange(customer_name: string, target_rate_pct: number): void {
    const period_keys = periods.map((p: TimePeriod) => p.period_key)
    const wanted = customer_name.trim().toLowerCase()

    function update_node(node: SalesHierarchyNode): SalesHierarchyNode {
      const trade_spend = read_trade_spend(node)
      const is_match = trade_spend && String(node.name ?? "").trim().toLowerCase() === wanted
      const children = (node as { children?: SalesHierarchyNode[] }).children
      const next_children = Array.isArray(children) ? children.map(update_node) : children
      if (!is_match) {
        return children === next_children ? node : ({ ...(node as any), children: next_children } as SalesHierarchyNode)
      }
      return {
        ...(node as any),
        children: next_children,
        metadata: {
          ...((node as any).metadata || {}),
          trade_spend: apply_trade_rate_to_customer(trade_spend, target_rate_pct, period_keys),
        },
      } as SalesHierarchyNode
    }

    setData((prev: SalesHierarchyNode[]) => prev.map(update_node))
    setHasUnsavedChanges(true)
  }

  /**
   * Fill missing Case Cost values from the catalog's per-case list price (the only
   * catalog price on a reliable case basis — see utils/catalog_case_costs.ts). Fill-only,
   * never touches revenue; an unsaved edit persisted by the normal Save.
   */
  async function handleFillCaseCostsFromCatalog(): Promise<void> {
    setIsFillingCaseCosts(true)
    try {
      // Empty query + max limit = the whole catalog (list_products caps at 100).
      const res = await searchProducts("", 100)
      if (!res.ok) {
        toast({ title: "Could not load the product catalog", description: res.error.message, variant: "destructive" })
        return
      }
      const products = res.value.data
      const plan = plan_case_cost_fill(data, products)
      if (plan.fills.length === 0) {
        toast({
          title: "No case costs to fill",
          description:
            plan.unmatched.length > 0
              ? `${plan.unmatched.length} SKU row${plan.unmatched.length === 1 ? "" : "s"} without a cost matched no catalog product with a list price (e.g. ${plan.unmatched.slice(0, 3).join(", ")}).`
              : `Every SKU row already carries a case cost (${plan.already_priced} checked).`,
        })
        return
      }
      setData((prev: SalesHierarchyNode[]) => apply_case_cost_fill(prev, plan.fills))
      setHasUnsavedChanges(true)
      setUpdateForecastDialogOpen(false)
      toast({
        title: `Filled ${plan.fills.length} case cost${plan.fills.length === 1 ? "" : "s"} from the catalog`,
        description: [
          "Revenue was not changed — only the cost column was set. Save to keep it.",
          plan.unmatched.length > 0 ? `${plan.unmatched.length} row(s) had no catalog match.` : null,
          products.length >= 100 ? "Catalog fetch capped at 100 products." : null,
        ]
          .filter(Boolean)
          .join(" "),
      })
    } finally {
      setIsFillingCaseCosts(false)
    }
  }

  /** Period span passed to the top-down API (forecast months from config start through plan year). */
  const top_down_period_caption = useMemo((): string | null => {
    const fp = periods.filter((p: TimePeriod) =>
      compare_period_to_forecast_start(p.period_key, forecastConfig.forecast_start_period)
    )
    if (fp.length === 0) {
      return null
    }
    return `${fp[0].period_key}–${fp[fp.length - 1].period_key}`
  }, [periods, forecastConfig.forecast_start_period])

  /** KPI cards use the same scenario-adjusted tree as the table so totals track opportunities and baseline/weighted/full views. */
  const displayForecastSummary = useMemo((): ForecastAPI.ForecastSummaryResponse | null => {
    if (!data.length) {
      return forecastSummary
    }
    const period_keys = periods.length > 0 ? periods.map((p: TimePeriod) => p.period_key) : null
    const live = summarizeForecastHierarchyTotals(processedData, period_keys, columnConfig)
    const total_vol = live.actual_volume_dollars + live.forecast_volume_dollars
    const target_raw = forecastSummary?.top_down_target_value
    const target_num =
      target_raw !== undefined && target_raw !== null && String(target_raw).length > 0
        ? Number(target_raw)
        : null
    const top_down_variance =
      target_num != null && Number.isFinite(target_num)
        ? String(total_vol - target_num)
        : forecastSummary?.top_down_variance

    return {
      version_id: forecastSummary?.version_id ?? currentVersionId ?? "",
      total_volume_dollars: String(total_vol),
      total_cases_sold: live.total_cases_sold,
      total_store_count: forecastSummary?.total_store_count ?? 0,
      forecast_volume_dollars: String(live.forecast_volume_dollars),
      actual_volume_dollars: String(live.actual_volume_dollars),
      total_promo_lift_dollars:
        show_promo_lift_in_summary && live.total_promo_lift_dollars > 0
          ? live.total_promo_lift_dollars
          : undefined,
      total_promo_lift_units:
        show_promo_lift_in_summary && live.total_promo_lift_units > 0
          ? live.total_promo_lift_units
          : undefined,
      top_down_target_value: forecastSummary?.top_down_target_value,
      top_down_variance,
    }
  }, [
    data.length,
    processedData,
    periods,
    forecastSummary,
    currentVersionId,
    columnConfig,
    show_promo_lift_in_summary,
  ])

  // Version management functions
  function saveVersionManagerToStorage(versionManager: VersionManager) {
    try {
      localStorage.setItem('sales_forecasting_versions', JSON.stringify(versionManager))
    } catch (err) {
      console.error("Error saving version manager to localStorage:", err)
    }
  }

  async function createBlankVersion(name: string, description?: string, scenario_kind?: string, year?: number, plan_kind?: string) {
    try {
      const { fiscal_year, forecast_start_period } =
        year != null && Number.isFinite(year)
          ? { fiscal_year: year, forecast_start_period: `${year}-01` }
          : default_blank_forecast_year_period(new Date())
      const kind = plan_kind || "forecast"
      const apiVersion = await ForecastAPI.createForecastVersion({
        name,
        description,
        scenario_kind,
        plan_kind: kind,
        // The first version of a plan kind is that plan's primary (a budget and a forecast each have
        // one) — when an admin creates it; a rep's version waits for an admin to choose it.
        is_primary:
          can_manage_forecast_versions(current_user, forecast_approvals_required) &&
          !versionManager.versions.some((v: ForecastVersion) => (v.plan_kind ?? "forecast") === kind),
        fiscal_year,
        forecast_start_period,
      })

      const newVersion = ForecastAPI.convertApiVersionToFrontend(apiVersion)

      const blank_save = await ForecastAPI.updateForecastHierarchy(newVersion.id, {
        nodes: [],
        save_context: {
          driver: "Created blank forecast version",
          notes: name.trim().length > 0 ? name.trim() : undefined,
        },
      })
      // The empty save bumps the server's updated_at; keep it for the next optimistic check.
      newVersion.updated_at = blank_save.updated_at
      setForecastConfig((prev) => ({ ...prev, forecast_start_period }))
      lastSavedDataSignatureRef.current = forecastDataSignature([])
      resetUndoHistory([])
      setHasUnsavedChanges(false)

      const updatedVersionManager = {
        ...versionManager,
        versions: [...versionManager.versions, newVersion],
        current_version_id: newVersion.id,
        primary_version_id: primary_forecast_version([...versionManager.versions, newVersion])?.id ?? null,
      }

      setVersionManager(updatedVersionManager)
      setCurrentVersionId(newVersion.id)
      await loadVersionData(newVersion.id, newVersion)
    } catch (error) {
      console.error("Error creating blank version:", error)
      setError("Failed to create version")
    }
  }

  function switchToVersion(versionId: string) {
    guardUnsavedChanges({
      title: "Discard unsaved forecast edits?",
      description:
        "Switching versions reloads the grid from the server, so the cell edits you have not saved will be lost.",
      confirm_label: "Discard and switch",
      run: () => performSwitchToVersion(versionId),
    })
  }

  async function performSwitchToVersion(versionId: string) {
    try {
      setCompareCells([])
      setCompareCellsArePreview(false)
      // Load version data from API
      await loadVersionData(versionId)
      
      // Update version manager state
      const updatedVersionManager = {
        ...versionManager,
        current_version_id: versionId
      }
      setVersionManager(updatedVersionManager)
      
      // Clear unsaved changes flag when switching versions
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Error switching to version:', error)
      setError('Failed to switch to version')
    }
  }

  async function updateVersion(versionId: string, name: string, description?: string, scenario_kind?: string) {
    try {
      const apiVersion = await ForecastAPI.updateForecastVersion(versionId, { name, description, scenario_kind })
      const updatedVersion = ForecastAPI.convertApiVersionToFrontend(apiVersion)
      setVersionManager(prev => ({
        ...prev,
        versions: prev.versions.map(v => v.id === versionId ? { ...v, ...updatedVersion } : v)
      }))
    } catch (error) {
      console.error('Error updating version:', error)
      setError('Failed to update version')
    }
  }

  /** Lock (or unlock) a version — the server refuses every write to a locked version. */
  async function setVersionLocked(versionId: string, locked: boolean) {
    try {
      const apiVersion = await ForecastAPI.updateForecastVersion(versionId, { locked })
      const updatedVersion = ForecastAPI.convertApiVersionToFrontend(apiVersion)
      setVersionManager(prev => ({
        ...prev,
        versions: prev.versions.map(v => v.id === versionId ? { ...v, ...updatedVersion } : v)
      }))
    } catch (error) {
      console.error('Error updating version lock:', error)
      setError('Failed to update version lock')
    }
  }

  /** Archive (or unarchive) a version — archived versions collapse out of the panel list. */
  async function setVersionArchived(versionId: string, archived: boolean) {
    try {
      const apiVersion = await ForecastAPI.updateForecastVersion(versionId, { archived })
      const updatedVersion = ForecastAPI.convertApiVersionToFrontend(apiVersion)
      setVersionManager(prev => ({
        ...prev,
        versions: prev.versions.map(v => v.id === versionId ? { ...v, ...updatedVersion } : v)
      }))
    } catch (error) {
      console.error('Error updating version archive state:', error)
      setError('Failed to update version archive state')
    }
  }

  /**
   * Fork the CURRENT grid — including unsaved edits — into a new child version and switch
   * to it. This is the "keep going from here as a scenario" gesture; plain Duplicate only
   * copies the last saved server state.
   */
  async function saveGridAsNewScenario(name: string, description?: string, scenario_kind?: string, plan_kind?: string) {
    const source = versionManager.versions.find(v => v.id === versionManager.current_version_id)
    if (!source) {
      setError("No current version to fork")
      return
    }
    try {
      const apiVersion = await ForecastAPI.createForecastVersion({
        name,
        description,
        scenario_kind: scenario_kind || source.scenario_kind || "operating_plan",
        plan_kind: plan_kind || source.plan_kind || "forecast",
        parent_version_id: source.id,
        fiscal_year: source.fiscal_year,
        forecast_start_period: forecastConfig.forecast_start_period || source.forecast_start_period,
        period_grain: source.period_grain,
      })
      const newVersion = ForecastAPI.convertApiVersionToFrontend(apiVersion)
      const normalizedData = normalizeHierarchyData(data)
      const save_result = await ForecastAPI.updateForecastHierarchy(newVersion.id, {
        nodes: normalizedData,
        save_context: {
          driver: `Forked as scenario from "${source.name}"`,
          notes: hasUnsavedChanges ? "Included unsaved grid edits from the source version" : undefined,
        },
      })
      newVersion.updated_at = save_result.updated_at
      setVersionManager(prev => ({
        ...prev,
        versions: [newVersion, ...prev.versions],
        current_version_id: newVersion.id,
      }))
      setCurrentVersionId(newVersion.id)
      // The new version's saved state IS the current grid, so nothing is unsaved anymore.
      lastSavedDataSignatureRef.current = forecastDataSignature(data)
      resetUndoHistory(data)
      setHasUnsavedChanges(false)
      await loadVersionData(newVersion.id, newVersion)
      toast({
        title: `Forked into "${name}"`,
        description: "The grid as you see it — including any unsaved edits — was saved into the new scenario.",
      })
    } catch (error) {
      console.error('Error forking version:', error)
      setError('Failed to fork the current grid into a new scenario')
    }
  }

  function deleteVersion(versionId: string) {
    if (versionManager.versions.length <= 1) {
      setError("Cannot delete the last version")
      return
    }
    if (versionManager.current_version_id === versionId) {
      // Deleting the open version reloads another one over the grid.
      guardUnsavedChanges({
        title: "Discard unsaved forecast edits?",
        description:
          "Deleting the open version loads another version into the grid, so the cell edits you have not saved will be lost.",
        confirm_label: "Discard and delete",
        run: () => performDeleteVersion(versionId),
      })
      return
    }
    void performDeleteVersion(versionId)
  }

  async function performDeleteVersion(versionId: string) {
    try {
      await ForecastAPI.deleteForecastVersion(versionId)
      const remainingVersions = versionManager.versions.filter(v => v.id !== versionId)
      const nextVersionId = versionManager.current_version_id === versionId ? remainingVersions[0]?.id || null : versionManager.current_version_id
      setVersionManager(prev => ({
        ...prev,
        versions: remainingVersions,
        current_version_id: nextVersionId,
        primary_version_id: primary_forecast_version(remainingVersions)?.id || null
      }))
      if (nextVersionId && versionManager.current_version_id === versionId) {
        const nextMeta = remainingVersions.find(v => v.id === nextVersionId)
        await loadVersionData(nextVersionId, nextMeta)
      }
    } catch (error) {
      console.error('Error deleting version:', error)
      setError('Failed to delete version')
    }
  }

  function duplicateVersion(versionId: string, name: string) {
    // The clone copies the last SAVED state and then loads it into the grid — without
    // the guard, "fork this as a scenario before I keep going" silently lost the edits.
    guardUnsavedChanges({
      title: "Discard unsaved forecast edits?",
      description:
        "Duplicating copies the last saved state of the version and opens the copy, so the cell edits you have not saved will be lost. Save first to include them in the copy.",
      confirm_label: "Discard and duplicate",
      run: () => performDuplicateVersion(versionId, name),
    })
  }

  async function performDuplicateVersion(versionId: string, name: string) {
    try {
      const originalVersion = versionManager.versions.find(v => v.id === versionId)
      const apiVersion = await ForecastAPI.cloneForecastVersion(versionId, {
        name,
        description: originalVersion?.description,
        scenario_kind: originalVersion?.scenario_kind || "operating_plan",
        fiscal_year: originalVersion?.fiscal_year
      })
      const newVersion = ForecastAPI.convertApiVersionToFrontend(apiVersion)
      setVersionManager(prev => ({
        ...prev,
        versions: [newVersion, ...prev.versions],
        current_version_id: newVersion.id
      }))
      await loadVersionData(newVersion.id, newVersion)
    } catch (error) {
      console.error('Error duplicating version:', error)
      setError('Failed to duplicate version')
    }
  }

  async function setPrimaryVersion(versionId: string) {
    try {
      const apiVersion = await ForecastAPI.updateForecastVersion(versionId, { is_primary: true })
      const updatedVersion = ForecastAPI.convertApiVersionToFrontend(apiVersion)
      setVersionManager(prev => {
        // Only the same plan kind loses its star: the primary budget stays when a forecast is chosen.
        const versions = prev.versions.map(v => ({
          ...v,
          is_primary:
            v.id === versionId ? true : (v.plan_kind ?? "forecast") === (updatedVersion.plan_kind ?? "forecast") ? false : v.is_primary,
          updated_at: v.id === versionId ? updatedVersion.updated_at : v.updated_at
        }))
        return { ...prev, versions, primary_version_id: primary_forecast_version(versions)?.id ?? null }
      })
    } catch (error) {
      console.error('Error setting primary version:', error)
      setError('Failed to set primary version')
    }
  }

  function requestSaveCurrentVersion(versionId: string): void {
    if (periods.length === 0) {
      setError("Cannot save until forecast periods are loaded")
      return
    }
    const version = versionManager.versions.find(v => v.id === versionId)
    if (version?.locked_at) {
      setError(`"${version.name}" is locked — unlock it in the version panel to save changes`)
      return
    }
    setSaveDialogBaseline(lastSavedPlanTotalsRef.current)
    setPendingSaveVersionId(versionId)
    setSaveConfirmOpen(true)
  }

  function handle_save_confirm_dialog_open_change(open: boolean): void {
    setSaveConfirmOpen(open)
    if (!open) {
      setPendingSaveVersionId(null)
      setSaveDialogBaseline(null)
    }
  }

  // Computed only while one of the two places that show it is open: the tree can hold thousands
  // of leaves, and the saved baseline is re-read from its signature.
  const unsaved_diff = useMemo((): UnsavedDiff | null => {
    if (!saveConfirmOpen && !showUnsavedReview) return null
    return diff_unsaved_forecast(saved_tree_from_signature(lastSavedDataSignatureRef.current), data)
  }, [saveConfirmOpen, showUnsavedReview, data])

  const save_confirm_current_totals = useMemo((): PlanTotalsSnapshot => {
    if (periods.length === 0) {
      return { total_dollars: 0, actual_dollars: 0, forecast_dollars: 0 }
    }
    return computePlanTotalsSnapshot(
      data,
      viewMode,
      opportunityScenarioView,
      selectedOpportunityIds,
      periods.map((p: TimePeriod) => p.period_key),
      columnConfig,
      showOpportunitiesPanel
    )
  }, [data, viewMode, opportunityScenarioView, selectedOpportunityIds, periods, columnConfig, showOpportunitiesPanel])

  /**
   * Map queued dialog customers to their saved forecast node so QuickBooks actuals land on
   * the new row without a separate trip to the mappings page. The stable key is resolved
   * from the saved tree (the dialog's node may have merged into an existing row); the
   * upsert is idempotent server-side, and failures are non-fatal (the mappings page
   * remains the manual fallback).
   */
  async function flushPendingCustomerAliases(savedNodes: SalesHierarchyNode[]): Promise<void> {
    const aliases = pendingCustomerAliasesRef.current
    if (aliases.length === 0) {
      return
    }
    pendingCustomerAliasesRef.current = []
    function findCustomerNode(nodes: SalesHierarchyNode[], name: string): SalesHierarchyNode | null {
      for (const node of nodes) {
        const level = String(node.level)
        if ((level === "customer" || level === "retailer") && node.name.trim().toLowerCase() === name.trim().toLowerCase()) {
          return node
        }
        const children = (node as { children?: SalesHierarchyNode[] }).children
        if (Array.isArray(children)) {
          const found = findCustomerNode(children, name)
          if (found) return found
        }
      }
      return null
    }
    for (const alias of aliases) {
      const node = findCustomerNode(savedNodes, alias)
      if (!node) continue
      try {
        await ForecastAPI.createForecastCustomerAlias(alias, (node as any).stable_key || node.id)
      } catch (err) {
        console.error("Failed to auto-map customer alias", alias, err)
      }
    }
  }

  async function executeHierarchySaveAfterConfirm(driver: string, notes: string): Promise<void> {
    const versionId = pendingSaveVersionId
    if (!versionId) {
      return
    }
    setIsSavingHierarchy(true)
    setError(null)
    try {
      const normalizedData = normalizeHierarchyData(data)
      const expected_updated_at = versionManager.versions.find((v: ForecastVersion) => v.id === versionId)?.updated_at

      const save_result = await ForecastAPI.updateForecastHierarchy(versionId, {
        nodes: normalizedData,
        ...(pendingTopDownCommit ? { top_down_commit: pendingTopDownCommit } : {}),
        ...(expected_updated_at ? { expected_updated_at } : {}),
        save_context: {
          driver: driver.trim(),
          notes: notes.length > 0 ? notes : undefined,
        },
      })

      if (hierarchy_save_outcome(save_result).kind === "queued") {
        // Nothing was written: a sales rep's save to the primary or a locked version waits for
        // an admin. The grid keeps the edits as they are (still unsaved here) — no reload.
        setSaveConfirmOpen(false)
        setPendingSaveVersionId(null)
        setSaveDialogBaseline(null)
        void mutate_swr(PENDING_CHANGE_REQUESTS_KEY)
        toast({
          title: "Sent for approval",
          description: "This version is the primary or locked, so an org admin has to approve your change before it is applied. Your edits stay in the grid.",
        })
        return
      }

      setPendingTopDownCommit(null)
      await flushPendingCustomerAliases(normalizedData)

      const updatedVersionManager = {
        ...versionManager,
        versions: versionManager.versions.map((v: ForecastVersion) =>
          v.id === versionId
            ? {
                ...v,
                updated_at: save_result.updated_at,
              }
            : v
        ),
      }

      setVersionManager(updatedVersionManager)
      lastSavedDataSignatureRef.current = forecastDataSignature(data)
      resetUndoHistory(data)
      setHasUnsavedChanges(false)
      // What was saved is no longer a draft.
      clear_forecast_draft(versionId)
      setDraftSavedAt(null)
      setCompareCellsArePreview(false)
      setSaveConfirmOpen(false)
      setPendingSaveVersionId(null)
      setSaveDialogBaseline(null)
      await loadVersionData(versionId)
      toast({
        title: "Forecast saved",
        description: pendingTopDownCommit ? "Saved hierarchy changes and top-down target metadata." : "Saved hierarchy changes.",
      })
    } catch (error) {
      console.error("Error saving version:", error)
      const message = error instanceof Error ? error.message : "Failed to save version"
      setError(message)
      toast({ title: "Save failed", description: message, variant: "destructive" })
    } finally {
      setIsSavingHierarchy(false)
    }
  }

  function clearStoredData() {
    try {
      localStorage.removeItem('sales_forecasting_data')
      localStorage.removeItem('sales_forecasting_has_data')
    } catch (error) {
      console.error('Failed to clear localStorage:', error)
    }
  }

  // Load data on component mount
  useEffect(() => {
    loadForecastingData()
  }, [])

  // Every change to the tree that undo/redo did not make is an undo step.
  useEffect(() => {
    const previous = undoPreviousDataRef.current
    undoPreviousDataRef.current = data
    if (applyingUndoRef.current) {
      applyingUndoRef.current = false
      return
    }
    if (isLoadingData || previous == null || previous === data) return
    undoHistoryRef.current = record_step(undoHistoryRef.current, previous)
    syncUndoCounts()
  }, [data])

  function handleUndo(): void {
    const step = undo_step(undoHistoryRef.current, data)
    if (!step) return
    undoHistoryRef.current = step.history
    applyingUndoRef.current = true
    setData(step.value)
    syncUndoCounts()
  }

  function handleRedo(): void {
    const step = redo_step(undoHistoryRef.current, data)
    if (!step) return
    undoHistoryRef.current = step.history
    applyingUndoRef.current = true
    setData(step.value)
    syncUndoCounts()
  }

  // ⌘Z / ⇧⌘Z (Ctrl+Z / Ctrl+Y) undo and redo grid edits — but not while typing in a box, where
  // the browser's own text undo belongs.
  useEffect(() => {
    function on_key(event: KeyboardEvent): void {
      const action = undo_key_action(event)
      if (!action) return
      const target = event.target as HTMLElement | null
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return
      event.preventDefault()
      if (action === "undo") handleUndo()
      else handleRedo()
    }
    window.addEventListener("keydown", on_key)
    return () => window.removeEventListener("keydown", on_key)
  })

  // Track dirty state vs last saved signature; persistence only via explicit Save (no background saves).
  // Deliberately not gated on `data.length` — an emptied tree is still an edit, and saving it would
  // delete every node server-side.
  useEffect(() => {
    if (currentVersionId && !isCreatingSalesSource && !isLoadingData && !isUpdatingForecasts) {
      const currentSignature = forecastDataSignature(data)
      const dirty_state = evaluate_forecast_dirty_state(currentSignature, lastSavedDataSignatureRef.current)
      if (dirty_state.adopt_baseline) {
        lastSavedDataSignatureRef.current = currentSignature
      }
      setHasUnsavedChanges(dirty_state.has_unsaved_changes)
    }
  }, [data, currentVersionId, isCreatingSalesSource, isLoadingData, isUpdatingForecasts])

  // Every unsaved edit is written to the browser shortly after it is made, so a dropped
  // connection, a failed save or a closed tab does not lose it: the next load of this version
  // offers the draft back. Nothing reaches the server until Save.
  useEffect(() => {
    if (!hasUnsavedChanges || !currentVersionId || isLoadingData) {
      return
    }
    const version_id = currentVersionId
    const timer = setTimeout(() => {
      const saved_at = new Date().toISOString()
      const written = write_forecast_draft({
        version_id,
        saved_at,
        version_updated_at: versionManager.versions.find((v: ForecastVersion) => v.id === version_id)?.updated_at,
        signature: forecastDataSignature(data),
        roots: data,
      })
      setDraftSavedAt(written ? saved_at : null)
    }, 800)
    return () => clearTimeout(timer)
  }, [data, hasUnsavedChanges, currentVersionId, isLoadingData])

  function restoreDraft(): void {
    if (!draftOffer) return
    setData(draftOffer.draft.roots as SalesHierarchyNode[])
    setHasData(draftOffer.draft.roots.length > 0)
    setDraftOffer(null)
  }

  function discardDraft(): void {
    if (currentVersionId) clear_forecast_draft(currentVersionId)
    setDraftOffer(null)
    setDraftSavedAt(null)
  }

  // Warn on tab close / reload while the grid holds unsaved cell edits.
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }
    function handle_before_unload(event: BeforeUnloadEvent): void {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", handle_before_unload)
    return () => window.removeEventListener("beforeunload", handle_before_unload)
  }, [hasUnsavedChanges])

  // Baseline plan totals (plan columns) after last clean load/save — used for save confirmation vs footer/KPIs.
  useEffect(() => {
    if (isLoadingData || hasUnsavedChanges) {
      return
    }
    if (!hasData || periods.length === 0) {
      // A blank version starts from $0. Returning early here kept the previous version's totals,
      // so the first save on a new 2027 plan read "Previous: $46,524,277" (the 2026 primary).
      lastSavedPlanTotalsRef.current = { total_dollars: 0, actual_dollars: 0, forecast_dollars: 0 }
      return
    }
    const pk = periods.map((p: TimePeriod) => p.period_key)
    lastSavedPlanTotalsRef.current = computePlanTotalsSnapshot(
      data,
      viewMode,
      opportunityScenarioView,
      selectedOpportunityIds,
      pk,
      columnConfig,
      showOpportunitiesPanel
    )
  }, [
    hasData,
    periods,
    hasUnsavedChanges,
    isLoadingData,
    data,
    viewMode,
    opportunityScenarioView,
    selectedOpportunityIds,
    columnConfig,
    showOpportunitiesPanel,
  ])

  // Note: We no longer load dummy data automatically
  // Data is now loaded from localStorage or created via the form

  async function loadForecastingData() {
    setLoading(true)
    setIsLoadingData(true)
    setError(null)

    try {
      // Generate periods dynamically (last 6 months + next calendar year)
      const generatedPeriods = generatePeriods()
      setPeriods(generatedPeriods)
      
      setComparisonPeriods([])

      // Load versions from API
      const apiVersions = await ForecastAPI.listForecastVersions()
      const versions = apiVersions.map(ForecastAPI.convertApiVersionToFrontend)
      // Reopen the version that was open last, not always the primary (editing the wrong one is easy).
      const primaryVersion = pick_initial_version(versions, read_last_opened_version_id())
      setVersionManager(prev => ({
        ...prev,
        versions,
        current_version_id: primaryVersion?.id || null,
        primary_version_id: primary_forecast_version(versions)?.id || null
      }))

      // Find primary version or use first version
      if (primaryVersion) {
        setCurrentVersionId(primaryVersion.id)
        await loadVersionData(primaryVersion.id, primaryVersion)
      } else {
        // No versions exist, start with empty state
        setData([])
        setHasData(false)
        lastSavedDataSignatureRef.current = forecastDataSignature([])
        resetUndoHistory([])
        setHasUnsavedChanges(false)
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred")
      console.error("Error loading forecasting data:", err)
    } finally {
      setLoading(false)
      setIsLoadingData(false)
    }
  }

  async function refreshForecastVersions() {
    const apiVersions = await ForecastAPI.listForecastVersions()
    const versions = apiVersions.map(ForecastAPI.convertApiVersionToFrontend)
    setVersionManager(prev => ({
      ...prev,
      versions,
      primary_version_id: primary_forecast_version(versions)?.id || null
    }))
  }

  /**
   * The unsaved-edits decision happens BEFORE the restore request (see
   * `confirm_before_changelog_restore`), so by the time this runs the reload is
   * unconditional — guarding here again would let "Cancel" leave stale edits
   * sitting on top of an already-reverted server state.
   */
  function handle_forecast_changelog_restored(apiVersion: ForecastAPI.ApiForecastVersion) {
    void apply_forecast_changelog_restored(apiVersion)
  }

  function confirm_before_changelog_restore(): Promise<boolean> {
    return confirm_discard_unsaved_edits(
      "Reverting replaces the version on the server and reloads the grid, so the cell edits you have not saved will be lost.",
      "Discard and revert"
    )
  }

  async function apply_forecast_changelog_restored(apiVersion: ForecastAPI.ApiForecastVersion) {
    const fv = ForecastAPI.convertApiVersionToFrontend(apiVersion)
    await refreshForecastVersions()
    setVersionManager((prev) => ({
      ...prev,
      current_version_id: fv.id,
    }))
    setCurrentVersionId(fv.id)
    await loadVersionData(fv.id, fv)
  }

  async function loadVersionData(versionId: string, versionMeta?: ForecastVersion) {
    setIsLoadingData(true)
    setCompareCellsArePreview(false)
    // A comparison against another version is scoped to the version it was started from.
    setVersionCompare(null)
    setVersionCompareLeaves(null)
    try {
      const response = await ForecastAPI.getForecastHierarchy(versionId)
      const version = versionMeta ?? versionManager.versions.find(v => v.id === versionId)
      
        // Convert API response to our data format recursively
        function transformNode(node: any): any {
          // Handle JSON response from backend
          const forecastConfig = node.forecast_config && typeof node.forecast_config === 'object' ? node.forecast_config : null
          const completeForecastConfig = forecastConfig ? {
            initial_doors: Number(forecastConfig.initial_doors || 0),
            same_store_sales_multiplier: Number(forecastConfig.same_store_sales_multiplier ?? 0.03),
            store_count_growth_multiplier: Number(forecastConfig.store_count_growth_multiplier ?? 0.02),
            initial_cases_per_month: Number(forecastConfig.initial_cases_per_month || 0),
            cost_per_case: Number(forecastConfig.cost_per_case || 0)
          } : {
            initial_doors: 0,
            same_store_sales_multiplier: 0.03,
            store_count_growth_multiplier: 0.02,
            initial_cases_per_month: 0,
            cost_per_case: 0
          }
          
          // Transform backend data to frontend format
          const salesDataRaw = Array.isArray(node.sales_data) ? node.sales_data : []
          const salesData = mergeForecastActualRowsForSamePeriod(salesDataRaw)
          const updatedSalesData = salesData.map((dataPoint: any) => {
            const { _paired_actual: pairedActual, ...forecastRow } = dataPoint as any

            const periodKey = forecastRow.period || forecastRow.period_key
            const forecastStartPeriod = version?.forecast_start_period || (forecastConfig as any)?.forecast_start_period
            // A paired cell's actual is authoritative — see the rule and its history on
            // `use_forecast_as_primary_for_paired_cell`.
            const useForecastPrimary = use_forecast_as_primary_for_paired_cell()

            const forecastDetail = parse_detail_metrics(forecastRow.detail_metrics)
            const actualDetail = pairedActual ? parse_detail_metrics(pairedActual.detail_metrics) : undefined

            const fm = pairedActual ? extractRawSalesMetricsForTransform(forecastRow, completeForecastConfig.cost_per_case) : null
            const am = pairedActual ? extractRawSalesMetricsForTransform(pairedActual, completeForecastConfig.cost_per_case) : null
            const singleMetrics = extractRawSalesMetricsForTransform(forecastRow, completeForecastConfig.cost_per_case)

            const weightedMetrics = pairedActual
              ? (useForecastPrimary ? fm! : am!)
              : singleMetrics

            const sourceKind = pairedActual
              ? (useForecastPrimary ? "forecast" : "actual")
              : (forecastRow.source_kind ||
                  (periodKey && forecastStartPeriod && compare_period_to_forecast_start(periodKey, forecastStartPeriod)
                    ? "forecast"
                    : "actual"))
            const isForecast = sourceKind !== "actual"

            const variance = fm && am ? {
              store_count_variance: am.store_count - fm.store_count,
              volume_dollars_variance: am.volume_dollars - fm.volume_dollars,
              cases_sold_variance: am.cases_sold - fm.cases_sold,
              store_count_variance_pct: fm.store_count ? (am.store_count - fm.store_count) / fm.store_count * 100 : 0,
              volume_dollars_variance_pct: fm.volume_dollars ? (am.volume_dollars - fm.volume_dollars) / fm.volume_dollars * 100 : 0,
              cases_sold_variance_pct: fm.cases_sold ? (am.cases_sold - fm.cases_sold) / fm.cases_sold * 100 : 0,
            } : undefined

            const opportunity = node.metadata?.opportunity
            // Clamp to [0, 100]; a 0% likelihood cannot be divided back out, so the
            // reconstruction below is skipped instead of inflating base metrics 100×.
            const likelihoodMultiplier = opportunity && opportunity.status !== "live"
              ? Math.max(0, Math.min(100, Number(opportunity.likelihood_pct ?? 100))) / 100
              : 1
            const savedOpportunityBaseMetrics = parseOpportunityBaseMetrics(forecastRow.manual_note)
            const opportunityBaseMetrics = forecastRow.opportunity_base_metrics || savedOpportunityBaseMetrics || (opportunity && opportunity.status !== "live" && likelihoodMultiplier > 0 ? {
              store_count: Math.round(weightedMetrics.store_count / likelihoodMultiplier),
              cases_sold: round_cases(weightedMetrics.cases_sold / likelihoodMultiplier),
              volume_dollars: weightedMetrics.volume_dollars / likelihoodMultiplier,
              case_cost: weightedMetrics.case_cost,
            } : undefined)
            
            const periodObj = periodKey ? parse_period_key_to_time_period(periodKey) : { year: 0, month: 0, period_key: "" }
            const primaryDetail = useForecastPrimary ? forecastDetail : (actualDetail ?? forecastDetail)

            return {
              ...forecastRow,
              period: periodObj,
              metrics: {
                store_count: weightedMetrics.store_count,
                cases_sold: weightedMetrics.cases_sold,
                volume_dollars: weightedMetrics.volume_dollars,
                units_sold: weightedMetrics.units_sold,
                case_cost: weightedMetrics.case_cost,
                detail: primaryDetail,
              },
              forecast_metrics: fm && am ? {
                store_count: fm.store_count,
                cases_sold: fm.cases_sold,
                volume_dollars: fm.volume_dollars,
                units_sold: fm.units_sold,
                case_cost: fm.case_cost,
                detail: forecastDetail,
              } : undefined,
              actual_metrics: fm && am ? {
                store_count: am.store_count,
                cases_sold: am.cases_sold,
                volume_dollars: am.volume_dollars,
                units_sold: am.units_sold,
                case_cost: am.case_cost,
                detail: actualDetail,
              } : undefined,
              variance,
              compare_axis: fm && am ? ("dollars" as const) : undefined,
              is_forecast: forecastRow.is_forecast !== undefined ? forecastRow.is_forecast : isForecast,
              is_actual: forecastRow.is_actual !== undefined ? forecastRow.is_actual : !isForecast,
              source_kind: sourceKind,
              // Provenance fields travel with the primary row (same rule as the lock flag):
              // saving must not overwrite an imported actual's data_source/manual_note.
              data_source: provenance_field_for_merged_row(
                forecastRow.data_source, pairedActual?.data_source, pairedActual, useForecastPrimary),
              is_locked: lock_flag_for_merged_row(forecastRow, pairedActual, useForecastPrimary),
              manual_note: provenance_field_for_merged_row(
                forecastRow.manual_note, pairedActual?.manual_note, pairedActual, useForecastPrimary),
              opportunity_base_metrics: opportunityBaseMetrics
            }
          })
          
          // Recursively transform children
          const transformedChildren = Array.isArray(node.children) 
            ? node.children.map((child: any) => transformNode(child))
            : []
          
          return {
            ...node,
            forecast_config: completeForecastConfig,
            sales_data: updatedSalesData,
            case_cost: Number(completeForecastConfig.cost_per_case),
            children: transformedChildren
          }
        }
        
        const hierarchyData = response.nodes.map(node => transformNode(node))
      
      // Normalize the data to combine duplicate hierarchy paths
      const normalizedData = normalizeHierarchyData(hierarchyData)
      const preSplice = aggregateDuplicateClassOfTrade(normalizedData)
      const splicedData = spliceOpportunityBranchesIntoExistingCustomers(preSplice)
      const aggregatedData = aggregateChildrenToParents(splicedData)
      const sortedData = sortHierarchyByVolume(aggregatedData)
      const calendarYear = calendarYearFromForecastVersion(
        version?.fiscal_year,
        version?.forecast_start_period ?? forecastConfig.forecast_start_period,
        new Date().getFullYear()
      )
      if (version?.forecast_start_period) {
        setForecastConfig((prev) => ({ ...prev, forecast_start_period: version.forecast_start_period! }))
      }
      const grain = version?.period_grain ?? "month"
      const allKeysFromHierarchy = collect_period_keys_from_hierarchy(sortedData)
      setAllPeriodKeys(allKeysFromHierarchy)
      setWeekColumnWindow({ kind: "rolling" })
      const yearPeriods: TimePeriod[] =
        grain === "month"
          ? buildJanDecPeriodsForYear(calendarYear)
          : (() => {
              const keys = filter_week_column_keys(allKeysFromHierarchy, { kind: "rolling" }, new Date())
              const visible = keys.length > 0 ? keys : allKeysFromHierarchy
              return visible.map((pk) => parse_period_key_to_time_period(pk))
            })()
      setPeriods(yearPeriods)

      const availability = scan_forecast_measure_availability(sortedData)
      setColumnConfig((prev) => reconcileColumnConfigWithAvailability(prev, availability))
      setPromoLiftCompare(false)
      const actualInYear = getActualPeriodsFromHierarchy(aggregatedData).filter(
        (p: TimePeriod) => p.year === calendarYear
      )
      setComparisonPeriods(actualInYear)
      
      
      setData(sortedData)
      setHasData(sortedData.length > 0)
      setCurrentVersionId(versionId)
      remember_last_opened_version_id(versionId)
      const loaded_signature = forecastDataSignature(sortedData)
      lastSavedDataSignatureRef.current = loaded_signature
      resetUndoHistory(sortedData)
      setHasUnsavedChanges(false)
      // A draft left in this browser is offered back only when it holds something the server
      // does not; one equal to the loaded grid is stale and dropped.
      const offer = offer_forecast_draft(read_forecast_draft(versionId), loaded_signature, version?.updated_at)
      if (!offer) clear_forecast_draft(versionId)
      setDraftOffer(offer)
      setDraftSavedAt(null)
      setVersionManager(prev => ({ ...prev, current_version_id: versionId }))
      await loadForecastSummary(versionId)
      // Keep version metadata (esp. updated_at) in sync: actualize, AI edits, and saves
      // bump it server-side, and the next save sends it as the optimistic-concurrency token.
      await refreshForecastVersions()

    } catch (err) {
      console.error('Error loading version data:', err)
      setError('Failed to load forecast data')
    } finally {
      setIsLoadingData(false)
    }
  }

  function sortHierarchyByVolume(nodes: SalesHierarchyNode[]): SalesHierarchyNode[] {
    function nodeVolume(node: any): number {
      return (node.sales_data || []).reduce((sum: number, dataPoint: any) => {
        return sum + Number(dataPoint.metrics?.volume_dollars || dataPoint.volume_dollars || 0)
      }, 0)
    }

    function sortNode(node: any): any {
      const children = Array.isArray(node.children)
        ? sortHierarchyByVolume(node.children as SalesHierarchyNode[])
        : node.children

      return { ...node, children }
    }

    return nodes
      .map((node: SalesHierarchyNode) => sortNode(node))
      .sort((a: any, b: any) => nodeVolume(b) - nodeVolume(a))
  }

  function getPeriodsFromHierarchy(nodes: SalesHierarchyNode[]): TimePeriod[] {
    const periodMap = new Map<string, TimePeriod>()

    function collectPeriods(node: any): void {
      if (Array.isArray(node.sales_data)) {
        node.sales_data.forEach((dataPoint: any) => {
          const period = dataPoint.period
          const periodKey = period?.period_key
          if (periodKey) {
            // parse_period_key_to_time_period understands every grain (2026-05, 2026-W05,
            // 2026-Q2) — splitting on "-" turned week keys into month: NaN.
            const parsed = parse_period_key_to_time_period(periodKey)
            periodMap.set(periodKey, {
              ...parsed,
              year: Number(period.year ?? parsed.year),
              month: Number(period.month ?? parsed.month),
            })
          }
        })
      }

      if (Array.isArray(node.children)) {
        node.children.forEach((child: any) => collectPeriods(child))
      }
    }

    nodes.forEach((node: SalesHierarchyNode) => collectPeriods(node))
    return Array.from(periodMap.values()).sort((a: TimePeriod, b: TimePeriod) => a.period_key.localeCompare(b.period_key))
  }

  function getActualPeriodsFromHierarchy(nodes: SalesHierarchyNode[]): TimePeriod[] {
    const periodMap = new Map<string, TimePeriod>()

    function collectActualPeriods(node: any): void {
      if (Array.isArray(node.sales_data)) {
        node.sales_data.forEach((dataPoint: any) => {
          const period = dataPoint.period
          const periodKey = period?.period_key
          const isActual = dataPoint.source_kind === "actual" || dataPoint.is_actual === true
          if (isActual && periodKey) {
            const parsed = parse_period_key_to_time_period(periodKey)
            periodMap.set(periodKey, {
              ...parsed,
              year: Number(period.year ?? parsed.year),
              month: Number(period.month ?? parsed.month),
            })
          }
        })
      }

      if (Array.isArray(node.children)) {
        node.children.forEach((child: any) => collectActualPeriods(child))
      }
    }

    nodes.forEach((node: SalesHierarchyNode) => collectActualPeriods(node))
    return Array.from(periodMap.values()).sort((a: TimePeriod, b: TimePeriod) => a.period_key.localeCompare(b.period_key))
  }

  async function loadForecastSummary(versionId: string): Promise<void> {
    try {
      const summary = await ForecastAPI.getForecastSummary(versionId)
      setForecastSummary(summary)
    } catch (error) {
      console.error('Error loading forecast summary:', error)
      setForecastSummary(null)
    }
  }

  function parseOpportunityBaseMetrics(manualNote?: string): any | undefined {
    if (!manualNote) return undefined
    try {
      const parsed = JSON.parse(manualNote)
      if (parsed?.kind === "opportunity_base_metrics" && parsed.metrics) {
        return parsed.metrics
      }
    } catch (_error) {
      return undefined
    }
    return undefined
  }

  async function exportData(exportKind: string) {
    try {
      // The ship-to grouping exists only in the grid (RTM allocations applied client-side),
      // so this export is built from the same hierarchy the Ship-to view renders.
      const is_ship_to_monthly =
        exportKind === SHIP_TO_MONTHLY_EXPORT_KIND || exportKind === SHIP_TO_SHIP_MONTH_EXPORT_KIND
      const blob = is_ship_to_monthly
        ? new Blob(
            [
              build_ship_to_revenue_by_month_csv(
                buildForecastTableHierarchy(data, ViewMode.SHIP_TO_VIEW, opportunityScenarioView, selectedOpportunityIds, {
                  include_open_opportunities: showOpportunitiesPanel,
                  ship_to_fallback,
                }),
                columnConfig,
                exportKind === SHIP_TO_SHIP_MONTH_EXPORT_KIND ? shipWeekLeadTime : null
              ),
            ],
            { type: 'text/csv' }
          )
        : currentVersionId
        ? await ForecastAPI.exportForecastVersion(currentVersionId, exportKind)
        : new Blob([generateCSVExport()], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `sales-forecasting-${exportKind}-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast({ title: "Forecast exported", description: "CSV download started." })

    } catch (err) {
      console.error("Error exporting data:", err)
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : "Could not export this forecast.",
        variant: "destructive",
      })
    }
  }

  function generateCSVExport(): string {
    const headers = ['Hierarchy', 'Level', 'Period', 'Store Count', 'Volume ($)']
    const rows = [headers.join(',')]
    
    // Generate CSV data from the hierarchy
    function addNodeToCSV(node: any, depth: number = 0) {
      const indent = '  '.repeat(depth)
      node.sales_data.forEach((dataPoint: any) => {
        rows.push([
          `"${indent}${node.name}"`,
          `"${node.level}"`,
          `"${dataPoint.period.period_key}"`,
          dataPoint.metrics.store_count,
          dataPoint.metrics.volume_dollars
        ].join(','))
      })
      
      if (node.children) {
        node.children.forEach((child: any) => addNodeToCSV(child, depth + 1))
      }
    }
    
    data.forEach(node => addNodeToCSV(node))
    return rows.join('\n')
  }

  async function applyTopDownTarget(): Promise<void> {
    if (!currentVersionId || !topDownTarget.trim() || periods.length === 0) {
      return
    }

    const targetValue = parse_top_down_target_input(topDownTarget)
    if (targetValue == null) {
      setError("Enter a valid top-down target")
      return
    }

    const forecastPeriods = periods.filter((period) =>
      compare_period_to_forecast_start(period.period_key, forecastConfig.forecast_start_period)
    )
    const startPeriod = forecastPeriods[0]?.period_key || periods[0].period_key
    const endPeriod = forecastPeriods[forecastPeriods.length - 1]?.period_key || periods[periods.length - 1].period_key

    const commitPayload: ForecastAPI.TopDownTargetRequest = {
      metric: "revenue",
      target_value: targetValue,
      allocation_method: "historical_mix",
      start_period: startPeriod,
      end_period: endPeriod,
    }

    try {
      const preview = await ForecastAPI.previewTopDownTarget(currentVersionId, commitPayload)
      const merged = mergeTopDownPreviewIntoHierarchy(data, preview.updates)
      const rolledUp = aggregateChildrenToParents(merged)
      setData(rolledUp)
      setPendingTopDownCommit(commitPayload)
      setCompareMode(true)
      setCompareCells([])
      setCompareCellsArePreview(true)
      setTopDownTarget("")
      setError(null)
      setUpdateForecastDialogOpen(false)
      toast({
        title: "Top-down preview applied",
        description: "Review the allocated forecast, then Save to persist it.",
      })
    } catch (error) {
      console.error("Error previewing top-down target:", error)
      const message = error instanceof Error ? error.message : "Failed to preview top-down target"
      setError(message)
      toast({ title: "Preview failed", description: message, variant: "destructive" })
    }
  }


  function handleHierarchyRowNameChange(rowId: string, name: string): void {
    setData((prev: SalesHierarchyNode[]) => updateNodeNameInHierarchy(prev, rowId, name))
  }

  function handleAddHierarchyRow(row: TableRow): void {
    if (viewMode !== ViewMode.CHAIN_RETAILER_VIEW) {
      return
    }
    if (row.original_retailer_id != null || row.original_dc_id != null) {
      return
    }
    const leaf_level = inferForecastLeafLevel(data)
    // SKUs are structured-only: adds at (or under) the product line go through the
    // catalog picker instead of inserting a rename-me blank row.
    if (leaf_level === HierarchyLevel.SKU && String(row.level) === HierarchyLevel.PRODUCT_LINE) {
      handleOpenAddSkuDialog(row)
      return
    }
    if (String(row.level) === HierarchyLevel.SKU) {
      const parent_pl_id = find_product_line_parent_id(data, row.id)
      if (parent_pl_id) {
        handleOpenAddSkuDialogForNodeId(parent_pl_id)
      }
      return
    }
    const { nodes: next_nodes, expand_ids } = addHierarchyRowsUnderContext(data, {
      context_row_id: row.id,
      context_row_level: row.level,
      leaf_level,
      periods,
      defaults: {
        forecast_start_period: forecastConfig.forecast_start_period,
        cost_per_case: default_case_cost_from_hierarchy(data),
        same_store_sales_multiplier: forecastConfig.same_store_sales_multiplier,
        store_count_growth_multiplier: forecastConfig.store_count_multiplier,
      },
    })
    if (expand_ids.length === 0) {
      window.alert(
        "Could not add a row from this row type. Use a class of trade, customer, product line, or SKU row."
      )
      return
    }
    const rolled_up = aggregateChildrenToParents(next_nodes)
    setData(rolled_up)
    setExpandedNodes((previous: Set<string>) => {
      const next = new Set(previous)
      expand_ids.forEach((id: string) => next.add(id))
      return next
    })
  }

  /** Structured SKU quick-add: catalog picker instead of renaming a blank row by hand. */
  function handleOpenAddSkuDialogForNodeId(node_id: string): void {
    const chain: string[] = []
    function walk(nodes: SalesHierarchyNode[], trail: string[]): boolean {
      for (const node of nodes) {
        const next_trail = [...trail, node.name]
        if (node.id === node_id) {
          chain.push(...next_trail)
          return true
        }
        const children = (node as { children?: SalesHierarchyNode[] }).children
        if (Array.isArray(children) && walk(children, next_trail)) {
          return true
        }
      }
      return false
    }
    walk(data, [])
    // The row is a product line: its name narrows the SKU picker to that line's products.
    setAddSkuTarget({
      row_id: node_id,
      path_caption: chain.length > 0 ? chain.join(" → ") : node_id,
      product_line: chain.length > 0 ? chain[chain.length - 1] : undefined,
    })
  }

  /** Reopen a line's setup (the pencil next to a generated line). */
  function handleOpenEditLine(row: TableRow): void {
    const path = node_path_by_id(data, row.id)
    const node = path[path.length - 1]
    if (!node) return
    const customer = path.find((n: SalesHierarchyNode) => String(n.level) === HierarchyLevel.CUSTOMER)
    setEditLineTarget({
      node,
      path_caption: path.map((n: SalesHierarchyNode) => n.name).join(" › "),
      customer_name: customer?.name ?? null,
    })
  }

  /**
   * Rewrite a line's plan months from its edited setup through the generator a new source uses
   * (same growth, same pricing, the plan's quantity basis), then roll the tree up again. A draft
   * like any grid edit: nothing reaches the server until Save.
   */
  function handleEditLineSave(node_id: string, setup: LineSetup): void {
    setData((prev: SalesHierarchyNode[]) => {
      const node = node_path_by_id(prev, node_id).pop()
      if (!node) return prev
      const { nodes: generated } = generateSalesSourceData(
        {
          classOfTrade: "",
          customer: "edit-line",
          routeToMarket: "",
          customerIsOther: true,
          planningBySku: true,
          isOpportunity: false,
          opportunityName: "",
          opportunityLikelihood: 0,
          growthExpectations: [
            {
              id: "edit-line",
              name: "",
              initialDoors: setup.initial_doors,
              startMonth: setup.start_period,
              storeCountGrowthMultiplier: setup.store_growth,
              productLineName: "edit-line",
              initialCasesPerMonth: setup.cases_per_store_per_month,
              costPerCase: setup.cost_per_case,
              sameStoreSalesMultiplier: setup.same_store_growth,
              productSku: (node as any).product_sku || node.name,
              skuName: node.name,
              doorsByPeriod: setup.doors_by_period,
            },
          ],
        } as CreateSalesSourceFormData,
        sales_source_periods,
        { quantity_basis: plan_quantity_basis(prev) }
      )
      const generated_leaf = generated[0]?.children?.[0]?.children?.[0]
      if (!generated_leaf) return prev
      const next = replace_node_by_id(prev, node_id, (current: any) => rebuild_line_plan(current, setup, generated_leaf.sales_data ?? []))
      return aggregateChildrenToParents(next)
    })
    setEditLineTarget(null)
    toast({ title: "Line updated", description: "Its plan months follow the new setup. Save to keep the change." })
  }

  function handleOpenAddSkuDialog(row: TableRow): void {
    handleOpenAddSkuDialogForNodeId(row.id)
  }

  function handleAddSkuFromCatalog(
    selection: import("./utils/hierarchy_add_row").CatalogSkuSelection,
    initial_doors: number,
    initial_cases_per_month: number,
    start_month: string
  ): void {
    if (!addSkuTarget) return
    const result = addCatalogSkuUnderRow(data, {
      target_row_id: addSkuTarget.row_id,
      sku: selection,
      initial_doors,
      initial_cases_per_month,
      start_month,
      periods,
    })
    if ("error" in result) {
      toast({ title: "Could not add the SKU", description: result.error, variant: "destructive" })
      return
    }
    setData(aggregateChildrenToParents(result.nodes) as SalesHierarchyNode[])
    setExpandedNodes((previous: Set<string>) => {
      const next = new Set(previous)
      result.expand_ids.forEach((id: string) => next.add(id))
      return next
    })
    setHasUnsavedChanges(true)
    setAddSkuTarget(null)
    toast({
      title: `Added ${selection.sku_name}`,
      description: selection.product_upc || selection.product_sku
        ? `Catalog identity attached — ${provider_label} actuals can land on this row. Save to keep it.`
        : "No catalog identity — the row will not receive actuals until the product exists in the catalog. Save to keep it.",
    })
  }

  function handleDeleteHierarchyRow(row: TableRow): void {
    if (row.original_retailer_id != null || row.original_dc_id != null) {
      return
    }
    const scopeLabel = row.has_children
      ? `"${row.name}" and every row nested under it`
      : `"${row.name}"`
    if (
      !window.confirm(
        `Remove ${scopeLabel} from this forecast version?\n\nYou can avoid persisting the change by not clicking Save, or restore from another version.`
      )
    ) {
      return
    }
    setData((prevData: SalesHierarchyNode[]) => {
      let updated: SalesHierarchyNode[]
      if (viewMode === ViewMode.PRODUCT_LINE_VIEW && row.source_ids && row.source_ids.length > 0) {
        updated = prevData
        for (const id of row.source_ids) {
          updated = removeHierarchySubtreeById(updated, id)
        }
      } else {
        updated = removeHierarchySubtreeById(prevData, row.id)
      }
      const finalData = aggregateChildrenToParents(updated)
      const validIds = collectAllHierarchyNodeIds(finalData)
      const remainingOppIds = new Set(collectForecastOpportunities(finalData).map((o: ForecastOpportunitySummary) => o.id))
      queueMicrotask(() => {
        setExpandedNodes((prevExpanded: Set<string>) => new Set([...prevExpanded].filter((id: string) => validIds.has(id))))
        setSelectedOpportunityIds((prevSel: Set<string>) => new Set([...prevSel].filter((id: string) => remainingOppIds.has(id))))
      })
      return finalData
    })
  }

  function handleDataChange(
    rowId: string,
    periodKey: string,
    field: EditableMetricField,
    value: number,
    options?: { also_period_values?: { period_key: string; value: number }[] },
  ): void {
    // A carry-forward arrives as one call carrying the later periods it resolved, so the whole
    // change lands in a single state update and the hierarchy is aggregated once — not once per
    // week, which on a 52-column weekly plan is the difference between instant and unusable.
    const edits = [{ period_key: periodKey, value }, ...(options?.also_period_values ?? [])]

    // Update the underlying data and re-aggregate
    setData(prevData => {
      let updatedData = prevData

      for (const edit of edits) {
        if (viewMode === ViewMode.PRODUCT_LINE_VIEW) {
          // For product line view, we need to handle both product lines and SKUs
          if (rowId.startsWith('sku-')) {
            // This is a SKU update - use the specialized function for aggregated SKUs
            updatedData = updateAggregatedSKUData(updatedData, rowId, edit.period_key, field, edit.value, columnConfig)
          } else {
            // This is a product line update - update all source nodes for the aggregated product line
            updatedData = updateAggregatedProductLineData(updatedData, rowId, edit.period_key, field, edit.value, columnConfig)
          }
        } else {
          // For other views, use the existing logic
          if (field === 'case_cost') {
            // Sets the node's case_cost and reprices its plan rows (actual/locked rows keep their dollars).
            updatedData = updateCaseCostInHierarchy(updatedData, rowId, edit.value)
          } else {
            // Surgical per-cell update: the edited data point's dollars are recomputed
            // inside updateDataInHierarchy (proportional repricing / cost fallback),
            // including the detail-slice values the grid actually displays.
            // No global volume pass — it used to rewrite every other row's dollars,
            // clobbering direct volume edits and imported revenue.
            updatedData = updateDataInHierarchy(updatedData, rowId, edit.period_key, field, edit.value, columnConfig)
          }
        }
      }

      // Re-aggregate the data — once for the whole carry-forward, not once per period.
      const finalData = aggregateChildrenToParents(updatedData)

      return finalData
    })

    if (edits.length > 1) {
      toast({
        title: "Applied to later periods",
        description: `${edits.length} periods updated. Save to keep the change.`,
      })
    }
  }

  function handleForecastConfigChange(rowId: string, newConfig: { same_store_sales_multiplier: number, store_count_growth_multiplier: number }): void {
    // One in-flight hierarchy update: skip dirty detection until regeneration finishes.
    setIsUpdatingForecasts(true)
    
    // Update the forecast configuration for the specific row and regenerate forecasts
    setData(prevData => {
      try {
        
        // First update the configuration for the specific row
        const updatedData = updateForecastConfigInHierarchy(prevData, rowId, newConfig)
        // Then regenerate forecasts for the specific row that was changed
        const regeneratedData = regenerateForecastsForRow(updatedData, rowId, newConfig)
        
        // Recalculate volume for all rows since multipliers affect store count and cases sold
        const volumeRecalculatedData = recalculateVolumeForAllRows(regeneratedData)
        
        // Re-aggregate the data to ensure parent nodes reflect the updated forecasts and volume
        const finalData = aggregateChildrenToParents(volumeRecalculatedData)
        
        return finalData
      } catch (error) {
        console.error('❌ Error updating forecast config:', error)
        if (error instanceof Error) {
          console.error('Error stack:', error.stack)
        }
        return prevData // Return unchanged data on error
      } finally {
        setIsUpdatingForecasts(false)
      }
    })
  }

  async function handleCreateSalesSource(formData: CreateSalesSourceFormData): Promise<void> {
    try {
      setIsCreatingSalesSource(true)
      // Generate the new sales source data
      // Write the new rows the way the plan's own rows are written: a plan that counts account
      // totals (and units) gets totals and units, priced like the same SKU elsewhere in it.
      const { nodes: newSalesSourceData, opportunityId: createdOpportunityId } = generateSalesSourceData(formData, sales_source_periods, {
        quantity_basis: plan_quantity_basis(data),
        sku_reference: (stable_key: string | undefined) => sku_plan_reference(data, stable_key),
      })

      // A customer picked from the accounting list carries the exact alias string
      // actualization needs — queue it for auto-mapping on the next save.
      if (formData.customer.trim() && !formData.customerIsOther && !formData.isOpportunity) {
        pendingCustomerAliasesRef.current = [
          ...pendingCustomerAliasesRef.current.filter((a: string) => a !== formData.customer),
          formData.customer,
        ]
      }
      
      // Add the new data to the existing data
      const dataWithForecasts = generateForecasts(newSalesSourceData, forecastConfig)
      const aggregatedNewData = aggregateChildrenToParents(dataWithForecasts)
      const updatedData = [...data, ...aggregatedNewData]
      
      // Normalize the combined data to merge duplicate hierarchy paths
      let normalizedData = normalizeHierarchyData(updatedData)
      normalizedData = aggregateDuplicateClassOfTrade(normalizedData)
      normalizedData = spliceOpportunityBranchesIntoExistingCustomers(normalizedData)
      normalizedData = aggregateChildrenToParents(normalizedData)

      // Update local state
      setData(normalizedData)
      setHasData(true)

      if (createdOpportunityId) {
        setShowOpportunitiesPanel(true)
        setSelectedOpportunityIds((previous: Set<string>) => {
          if (previous.has(createdOpportunityId)) {
            return previous
          }
          const next = new Set(previous)
          next.add(createdOpportunityId)
          return next
        })
        const hierarchyForExpand = buildForecastTableHierarchy(
          normalizedData,
          viewMode,
          opportunityScenarioView,
          selectedOpportunityIds
        )
        const expandIds = collectExpandedAncestorIdsForOpportunityId(hierarchyForExpand, createdOpportunityId)
        setExpandedNodes((prev: Set<string>) => {
          const next = new Set(prev)
          expandIds.forEach((id: string) => next.add(id))
          return next
        })
      }
      
      // User must Save to persist new sources to the server.
      if (currentVersionId) {
        setHasUnsavedChanges(true)
      }
      toast({
        title: createdOpportunityId ? "Opportunity added" : "Sales source added",
        description: "Review the forecast impact, then Save to persist it.",
      })
    } catch (error) {
      console.error('Error creating sales source:', error)
      const message = error instanceof Error ? error.message : "Failed to create sales source"
      setError(message)
      toast({ title: "Could not create sales source", description: message, variant: "destructive" })
    } finally {
      setIsCreatingSalesSource(false)
    }
  }

  function handleQuickCreateOpportunity(row: TableRow): void {
    setQuickCreatePrefill(buildOpportunityPrefillFromHierarchyRow(row, data))
    setQuickCreateOpenSignal(Date.now())
  }

  function handleQuickCreateSalesSourceFromRow(row: TableRow): void {
    setQuickCreatePrefill(buildSalesSourcePrefillFromHierarchyRow(row, data))
    setQuickCreateOpenSignal(Date.now())
  }

  /**
   * The "Add new line" button. Opens the create dialog on a blank form — the workflow was
   * previously only reachable through a row's kebab menu or two levels down inside
   * "Update forecast", which is why nobody found it.
   */
  function handleNewCustomer(kind: "consumption" | "ship_to"): void {
    setQuickCreateCustomerKind(kind)
    setQuickCreatePrefill({ isOpportunity: false })
    setQuickCreateOpenSignal(Date.now())
  }

  function handleOpportunityLikelihoodChange(opportunityId: string, likelihoodPct: number): void {
    setData((prevData: SalesHierarchyNode[]) =>
      aggregateChildrenToParents(updateOpportunityLikelihood(prevData, opportunityId, likelihoodPct) as any) as SalesHierarchyNode[]
    )
  }

  function handlePromoteOpportunity(opportunityId: string): void {
    setData((prevData: SalesHierarchyNode[]) =>
      aggregateChildrenToParents(
        cleanupPromotedOpportunityPlaceholders(promoteOpportunityToLive(prevData, opportunityId)) as any
      ) as SalesHierarchyNode[]
    )
    setSelectedOpportunityIds(prev => {
      const next = new Set(prev)
      next.delete(opportunityId)
      return next
    })
  }

  function handleDeleteOpportunity(opportunityId: string): void {
    setData((prevData: SalesHierarchyNode[]) =>
      aggregateChildrenToParents(removeOpportunity(prevData, opportunityId) as any) as SalesHierarchyNode[]
    )
    setSelectedOpportunityIds(prev => {
      const next = new Set(prev)
      next.delete(opportunityId)
      return next
    })
  }

  /** Record the loss without erasing it: rows stay (zeroed), the deal moves to Closed. */
  function handleMarkOpportunityLost(opportunityId: string): void {
    setData((prevData: SalesHierarchyNode[]) =>
      aggregateChildrenToParents(markOpportunityLost(prevData, opportunityId) as any) as SalesHierarchyNode[]
    )
    setSelectedOpportunityIds(prev => {
      const next = new Set(prev)
      next.delete(opportunityId)
      return next
    })
  }

  function handleReopenOpportunity(opportunityId: string): void {
    setData((prevData: SalesHierarchyNode[]) =>
      aggregateChildrenToParents(reopenOpportunity(prevData, opportunityId) as any) as SalesHierarchyNode[]
    )
  }

  /** Expand the opportunity's ancestors and scroll the grid to its first row. */
  function handleViewOpportunityInForecast(opportunityId: string): void {
    const tableHierarchy = buildForecastTableHierarchy(data, viewMode, opportunityScenarioView, selectedOpportunityIds)
    const expandIds = collectExpandedAncestorIdsForOpportunityId(tableHierarchy, opportunityId)
    setExpandedNodes((previous: Set<string>) => {
      const next = new Set(previous)
      expandIds.forEach((id: string) => next.add(id))
      return next
    })
    // Wait a tick for the expanded rows to render before looking the row up in the DOM.
    setTimeout(() => {
      document
        .querySelector(`[data-opportunity-id="${CSS.escape(opportunityId)}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 150)
  }

  /** Expands ancestor rows so opportunity lines are visible in the grid (paths match `processedData`). */
  function expandTableRowsToShowOpenOpportunities(): void {
    const tableHierarchy = buildForecastTableHierarchy(data, viewMode, opportunityScenarioView, selectedOpportunityIds)
    const expandIds = collectExpandedAncestorIdsForAllOpenOpportunities(tableHierarchy)
    setExpandedNodes((previous: Set<string>) => {
      const next = new Set(previous)
      expandIds.forEach((id: string) => next.add(id))
      return next
    })
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${className || ''}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sales forecasting data...</p>
        </div>
      </div>
    )
  }

  // Only a failed *load* takes the whole page: once a grid is up, an error (a refused save, most
  // often) shows above it and the grid — with every unsaved source — stays. The full-page screen
  // used to replace the grid, and Retry reloaded the server's copy, so a 422 on save threw away
  // the sources a planner had just built (1st Phorm, 10/27).
  if (error && !hasData) {
    return (
      <div className={`flex items-center justify-center h-64 ${className || ''}`}>
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <Button onClick={loadForecastingData} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-6 px-6 ${className || ''}`}>
      {error ? (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          <span>
            <span className="font-medium">Not saved: </span>
            {error}
            {hasUnsavedChanges ? " Your edits are still on the grid — fix the problem and save again." : ""}
          </span>
          <Button type="button" variant="ghost" size="sm" className="shrink-0 text-red-800" onClick={() => setError(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}
      {/* Header */}
      <div className="mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-4">
          {buildForecastSummaryCards(
            displayForecastSummary,
            opportunity_kpi_detail_lines,
            plan_column_range_caption,
            show_promo_lift_in_summary,
            gross_to_net,
            trade_lift,
            period_keys_span_years(periods.map((p: TimePeriod) => p.period_key))
          ).map(
            (card: ForecastSummaryCardProps) => (
              <ForecastSummaryCard
                key={card.label}
                label={card.label}
                value={card.value}
                sub_label={card.sub_label}
                detail_lines={card.detail_lines}
              />
            )
          )}
        </div>
        {gross_to_net ? (
          <GrossToNetPanel
            bridge={gross_to_net}
            caption={plan_column_range_caption ? `Trade spend plan · ${plan_column_range_caption}` : undefined}
            className="mb-4"
            on_customer_rate_change={handleTradeRateChange}
          />
        ) : null}
        {trade_lift && showTradeLift ? (
          <TradeLiftPanel
            summary={trade_lift}
            caption={plan_column_range_caption ?? undefined}
            className="mb-4"
            period_labels={trade_lift_period_labels}
            promotion_ids_by_remote_id={promotionIdsByRemoteId}
            on_open_promotion={(id: string) => void handleOpenPromotionFromTradeLift(id)}
          />
        ) : null}
        {showBudgetVsActual && currentVersionId ? (
          <BudgetVsActualPanel
            version_id={currentVersionId}
            budget_version_id={budgetVsActualVersionId}
            versions={versionManager.versions}
            on_budget_version_change={setBudgetVsActualVersionId}
            className="mb-4"
          />
        ) : null}
        {draftOffer ? (
          <div
            className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            data-testid="forecast-draft-banner"
          >
            <span>
              This browser has unsaved edits to this version from{" "}
              {new Date(draftOffer.draft.saved_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}.
              {draftOffer.version_changed_since
                ? " The version was saved since then; restoring puts your edits over that save."
                : ""}
            </span>
            <span className="flex items-center gap-2">
              <Button type="button" size="sm" onClick={restoreDraft}>
                Restore draft
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={discardDraft}>
                Discard
              </Button>
            </span>
          </div>
        ) : null}
        <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <ToolbarGroup label="Version">
            <div className="flex flex-wrap items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsVersionManagerOpen(!isVersionManagerOpen)}
                className="h-9 min-w-0 max-w-64 justify-start gap-2 bg-gray-50 px-3 text-left hover:bg-gray-100"
              >
                <span className="truncate text-sm font-medium text-gray-900">
                  {versionManager.versions.find(v => v.id === versionManager.current_version_id)?.name || "Untitled"}
                </span>
                {versionManager.versions.find(v => v.id === versionManager.current_version_id)?.is_primary && (
                  <span className="shrink-0 rounded bg-yellow-200 px-2 py-1 text-xs text-yellow-800">Primary</span>
                )}
                {versionManager.versions.find(v => v.id === versionManager.current_version_id)?.locked_at && (
                  <span className="shrink-0 rounded bg-gray-200 px-2 py-1 text-xs text-gray-700">Locked</span>
                )}
              </Button>
              <Button
                onClick={async () => {
                  if (versionManager.current_version_id) {
                    requestSaveCurrentVersion(versionManager.current_version_id)
                  }
                }}
                disabled={
                  !versionManager.current_version_id ||
                  Boolean(versionManager.versions.find(v => v.id === versionManager.current_version_id)?.locked_at)
                }
                title={
                  versionManager.versions.find(v => v.id === versionManager.current_version_id)?.locked_at
                    ? "This version is locked — unlock it in the version panel to save"
                    : undefined
                }
                size="sm"
                variant={hasUnsavedChanges ? "default" : "outline"}
              >
                <Save className="h-4 w-4 mr-2" />
                {hasUnsavedChanges ? 'Save*' : 'Save'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                disabled={undoCounts.past === 0}
                onClick={handleUndo}
                title="Undo (⌘Z)"
                aria-label="Undo"
              >
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                disabled={undoCounts.future === 0}
                onClick={handleRedo}
                title="Redo (⇧⌘Z)"
                aria-label="Redo"
              >
                <Redo2 className="h-4 w-4" />
              </Button>
              {hasUnsavedChanges ? (
                <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setShowUnsavedReview(true)}>
                  Review changes
                </Button>
              ) : null}
              {hasUnsavedChanges && draft_saved_caption(draftSavedAt) ? (
                <span className="text-[11px] text-gray-500" title="Your edits are kept in this browser until you save or discard them.">
                  {draft_saved_caption(draftSavedAt)}
                </span>
              ) : null}
            </div>
          </ToolbarGroup>

          {rep_names.length > 0 ? (
            <ToolbarGroup label="Rep">
              <Select value={repFilter} onValueChange={(value: string) => setRepFilter(value)}>
                <SelectTrigger className="h-9 w-48 bg-white text-sm" aria-label="Show one rep's customers">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_REPS}>All reps</SelectItem>
                  {rep_names.map((name: string) => (
                    <SelectItem key={name} value={name}>
                      {name === my_rep_name ? `${name} (me)` : name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ToolbarGroup>
          ) : null}

          <ToolbarGroup label="Add">
            {/* One entry: the dialog branches by what the picked account is (its record decides
                consumption vs ship-to) and carries the opportunity option. Items go under Update
                forecast, versions under the Versions button — the five-way menu lost the sales team. */}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="flex items-center gap-2"
              title="Add a customer, chain or sales opportunity to this forecast"
              onClick={() => handleNewCustomer("consumption")}
            >
              <Plus className="h-4 w-4" />
              Add new line
            </Button>
          </ToolbarGroup>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            <ToolbarGroup label="Forecast">
              <Button
                type="button"
                size="sm"
                disabled={!currentVersionId}
                onClick={() => setUpdateForecastDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <TrendingUp className="h-4 w-4" />
                Update forecast
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!currentVersionId}
                onClick={() => setIsChatOpen(true)}
                className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
              >
                <Wand2 className="h-4 w-4" />
                ForecastWiz
              </Button>
              <Button
                type="button"
                size="sm"
                variant={showBudgetVsActual ? "default" : "outline"}
                onClick={() => setShowBudgetVsActual((value: boolean) => !value)}
                disabled={!currentVersionId}
                title="Budget, actual, variance and gross margin per customer for the periods that have actuals"
                className="flex items-center gap-2"
              >
                <Scale className="h-4 w-4" />
                Budget vs actual
              </Button>
              {trade_lift ? (
                <Button
                  type="button"
                  size="sm"
                  variant={showTradeLift ? "default" : "outline"}
                  onClick={() => setShowTradeLift((value: boolean) => !value)}
                  title="Show the promotions behind this plan: planned spend, the volume it is expected to buy, and the return"
                  className="flex items-center gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Trade lift
                </Button>
              ) : null}
              {is_week_grain ? (
                <Button
                  type="button"
                  size="sm"
                  variant={shipWeekView ? "default" : "outline"}
                  onClick={() => setShipWeekView((value: boolean) => !value)}
                  title="Relabel each week column with the week the product must ship to sell in that consumption week (columns shift back by the lead time; the numbers stay put)"
                  className="flex items-center gap-2"
                >
                  <Truck className="h-4 w-4" />
                  Ship weeks
                </Button>
              ) : null}
              {show_week_column_window ? (
                <div className="flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5">
                  <Button
                    type="button"
                    size="sm"
                    variant={weekColumnWindow.kind === "rolling" ? "default" : "ghost"}
                    className="h-7 px-2 text-xs"
                    title="From this week through the last week in the plan"
                    onClick={() => setWeekColumnWindow({ kind: "rolling" })}
                  >
                    Rolling
                  </Button>
                  {week_column_years.map((year: number) => (
                    <Button
                      key={year}
                      type="button"
                      size="sm"
                      variant={weekColumnWindow.kind === "year" && weekColumnWindow.year === year ? "default" : "ghost"}
                      className="h-7 px-2 text-xs"
                      onClick={() => setWeekColumnWindow({ kind: "year", year })}
                    >
                      {year}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    variant={weekColumnWindow.kind === "all" ? "default" : "ghost"}
                    className="h-7 px-2 text-xs"
                    title="Every week in the plan"
                    onClick={() => setWeekColumnWindow({ kind: "all" })}
                  >
                    All
                  </Button>
                </div>
              ) : null}
              <ForecastChangelogDialog
                version_id={currentVersionId}
                confirm_before_restore={confirm_before_changelog_restore}
                on_restored={handle_forecast_changelog_restored}
              />
              {forecast_approvals_required ? (
              <ForecastApprovalQueue
                on_applied={async (version_id: string | null) => {
                  await refreshForecastVersions()
                  if (version_id && version_id === currentVersionId && !hasUnsavedChanges) {
                    await loadVersionData(version_id)
                  }
                }}
              />
              ) : null}
            </ToolbarGroup>
            <CreateSalesSourceDialog
              onSave={handleCreateSalesSource}
              periods={sales_source_periods}
              existing_hierarchy_roots={data}
              initialData={quickCreatePrefill}
              openSignal={quickCreateOpenSignal}
              initialCustomerKind={quickCreateCustomerKind}
              triggerVariant="outline"
              triggerSize="sm"
              triggerClassName="hidden"
            />
          </div>
        </div>

      </div>

      {showOpportunitiesPanel && (opportunities.length > 0 || closed_opportunities.length > 0) && (
        <OpportunityImpactPanel
          opportunities={opportunities}
          closed_opportunities={closed_opportunities}
          scenarioView={opportunityScenarioView}
          selectedOpportunityIds={selectedOpportunityIds}
          onViewInForecast={handleViewOpportunityInForecast}
          onScenarioViewChange={setOpportunityScenarioView}
          onToggleOpportunity={(opportunityId: string) => {
            setSelectedOpportunityIds(prev => {
              const next = new Set(prev)
              if (next.has(opportunityId)) {
                next.delete(opportunityId)
              } else {
                next.add(opportunityId)
              }
              return next
            })
          }}
          onLikelihoodChange={handleOpportunityLikelihoodChange}
          onPromote={handlePromoteOpportunity}
          onMarkLost={handleMarkOpportunityLost}
          onReopen={handleReopenOpportunity}
          onDelete={handleDeleteOpportunity}
        />
      )}



      {/* Empty State or Main Table */}
      {!hasData ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
              <TrendingUp className="h-8 w-8 text-gray-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Sales Data</h3>
              <p className="text-gray-600 mb-4">
                Get started by creating your first sales source to begin forecasting.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                This will help you set up your sales forecasting with real data.
              </p>
              <div className="flex justify-center">
                <Button type="button" onClick={() => setUpdateForecastDialogOpen(true)}>
                  Update forecast
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {versionCompare && versionCompareLeaves ? (
            // The comparison is its own table, in place of the grid (which stays mounted below,
            // hidden, so its expansion and scroll come back untouched on "Back to grid").
            <VersionCompareView
              className="mb-3"
              this_version={this_version_compare_leaves}
              compared_version={versionCompareLeaves}
              periods={periods}
              labels={{ a: "This version", b: versionCompare.name }}
              year_shift={versionCompare.year_shift}
              on_back={stopVersionCompare}
            />
          ) : null}
          {shipWeekView && is_week_grain ? (
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-2.5">
              <Truck className="h-4 w-4 text-sky-700" />
              <span className="text-sm text-sky-900">
                Columns are labelled by <strong>ship week</strong> — the week product must leave to
                sell in its consumption week,
              </span>
              <label className="flex items-center gap-1.5 text-sm text-sky-900">
                <Input
                  type="number"
                  min={0}
                  max={12}
                  value={shipWeekLeadTime}
                  onChange={(e) => {
                    const value = Math.max(0, Math.min(12, Number(e.currentTarget.value) || 0))
                    setShipWeekLeadTime(value)
                    localStorage.setItem("forecast_ship_week_lead_time", String(value))
                  }}
                  className="h-7 w-14 bg-white text-center text-sm"
                />
                week{shipWeekLeadTime === 1 ? "" : "s"} earlier. The numbers themselves don't move.
              </label>
            </div>
          ) : null}
          {show_trade_spend && currentVersionId ? <TradeSpendPanel version_id={currentVersionId} className="mb-3" /> : null}
          {/* Main Table - Full Width (hidden, not unmounted, while a version comparison is open) */}
          <div className={versionCompare ? "hidden" : "-mx-6"}>
            <SalesForecastingTable
              data={displayData}
              periods={periods}
              fillForwardPeriodKeys={allPeriodKeys}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              forecastConfig={forecastConfig}
              compareMode={compareMode}
              comparisonPeriods={comparisonPeriods}
              columnConfig={columnConfig}
              shipWeekLeadTime={shipWeekView && is_week_grain ? shipWeekLeadTime : null}
              promo_lift_compare={promo_lift_compare}
              reviewToolbar={
                <ToolbarGroup label="Review">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {measure_availability.has_detail ? (
                      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700">
                        <span className="font-medium text-gray-500">Slice</span>
                        {can_toggle_consumption_delivered ? (
                          <div className="flex gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setColumnConfig((c) => ({
                                  ...c,
                                  forecast_dollar_basis: "consumption_gross_sales",
                                  forecast_case_basis: "forecasted_consumption_cases",
                                  ...(measure_availability.forecast_units
                                    ? { forecast_unit_basis: "forecasted_consumption_units" as const }
                                    : {}),
                                }))
                              }
                              className={`h-7 px-2 text-xs ${
                                (columnConfig.forecast_dollar_basis ?? "consumption_gross_sales") ===
                                "consumption_gross_sales"
                                  ? "bg-blue-50 text-blue-900"
                                  : ""
                              }`}
                            >
                              Consumption
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setColumnConfig((c) => ({
                                  ...c,
                                  forecast_dollar_basis: "gross_sales_delivered",
                                  forecast_case_basis: "forecasted_delivery_cases",
                                }))
                              }
                              className={`h-7 px-2 text-xs ${
                                columnConfig.forecast_dollar_basis === "gross_sales_delivered"
                                  ? "bg-blue-50 text-blue-900"
                                  : ""
                              }`}
                            >
                              Delivered
                            </Button>
                          </div>
                        ) : measure_availability.consumption_dollars && measure_availability.consumption_cases ? (
                          <span className="text-xs text-gray-600">Consumption</span>
                        ) : measure_availability.delivered_dollars && measure_availability.delivery_cases ? (
                          <span className="text-xs text-gray-600">Delivered</span>
                        ) : null}
                      </div>
                    ) : null}
                    {can_compare ? (
                      <div className="flex rounded-md border border-gray-300 bg-gray-100 p-1">
                        <Button
                          variant="ghost"
                          onClick={() => setCompareMode(false)}
                          size="sm"
                          className={`border px-3 text-sm ${!compareMode ? "border-blue-300 bg-blue-50 text-blue-800 shadow-sm hover:bg-blue-50" : "border-transparent text-gray-600 hover:bg-gray-200"}`}
                        >
                          Forecast
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setCompareMode(true)
                            setPromoLiftCompare(false)
                          }}
                          size="sm"
                          className={`border px-3 text-sm ${compareMode ? "border-blue-300 bg-blue-50 text-blue-800 shadow-sm hover:bg-blue-50" : "border-transparent text-gray-600 hover:bg-gray-200"}`}
                        >
                          Compare
                        </Button>
                      </div>
                    ) : null}
                    {(measure_availability.baseline_units || measure_availability.incremental_promo_units) &&
                    !is_delivered_detail_channel(columnConfig) &&
                    columnConfig.quantity_mode !== "packs" &&
                    effective_quantity_mode(columnConfig) === "units" ? (
                      <Button
                        type="button"
                        variant={promo_lift_compare ? "default" : "outline"}
                        size="sm"
                        className="whitespace-nowrap"
                        onClick={() => {
                          setPromoLiftCompare((v) => {
                            const next = !v
                            if (next) {
                              setCompareMode(false)
                            }
                            return next
                          })
                        }}
                      >
                        Promo lift
                      </Button>
                    ) : null}
                  </div>
                </ToolbarGroup>
              }
              tableToolbarExtra={
                <>
                <Button
                  type="button"
                  variant={show_trade_spend ? "default" : "outline"}
                  size="sm"
                  className="whitespace-nowrap"
                  title="Trade spend by customer and month, from the promo calendar"
                  onClick={() => set_show_trade_spend((shown: boolean) => !shown)}
                >
                  Trade spend
                </Button>
                {opportunities.length > 0 || closed_opportunities.length > 0 ? (
                  <Button
                    type="button"
                    variant={showOpportunitiesPanel ? "default" : "outline"}
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={() => {
                      const opening = !showOpportunitiesPanel
                      setShowOpportunitiesPanel(opening)
                      if (opening) {
                        expandTableRowsToShowOpenOpportunities()
                        // The cards render far above this toggle; move the viewport there so the
                        // click has visible feedback instead of silently reflowing off-screen.
                        setTimeout(() => {
                          document.getElementById("sales-opportunities-panel")?.scrollIntoView({ behavior: "smooth", block: "start" })
                        }, 100)
                      }
                    }}
                  >
                    Opportunities ({opportunities.length})
                    {closed_opportunities.length > 0 ? (
                      <span className="ml-1.5 rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
                        {closed_opportunities.length} closed
                      </span>
                    ) : null}
                  </Button>
                ) : null}
                </>
              }
              compareBadgeLabels={
                versionCompare
                  ? { actual: "This version", forecast: versionCompare.name }
                  : pendingTopDownCommit
                    ? { actual: "Allocated", forecast: "Prior" }
                    : undefined
              }
              compareFooterSubsetCaption={
                versionCompare
                  ? `This version vs ${versionCompare.name} (delta = this − compared)`
                  : pendingTopDownCommit
                    ? "Compared leaf rows only (prior forecast vs allocated preview)"
                    : undefined
              }
              onDataChange={handleDataChange}
              onForecastConfigChange={handleForecastConfigChange}
              expandedNodes={expandedNodes}
              onToggleExpansion={(nodeId: string) => {
                setExpandedNodes(prev => {
                  const newSet = new Set(prev)
                  if (newSet.has(nodeId)) {
                    newSet.delete(nodeId)
                  } else {
                    newSet.add(nodeId)
                  }
                  return newSet
                })
              }}
              onExpandAll={() => {
                const allNodeIds = new Set<string>()
                function collectNodeIds(nodes: any[]) {
                  nodes.forEach(node => {
                    allNodeIds.add(node.id)
                    if (node.children) collectNodeIds(node.children)
                  })
                }
                collectNodeIds(processedData)
                setExpandedNodes(allNodeIds)
              }}
              onCollapseAll={() => {
                setExpandedNodes(new Set())
              }}
              onColumnConfigChange={setColumnConfig}
              onCreateOpportunityFromRow={handleQuickCreateOpportunity}
              onCreateSalesSourceFromRow={handleQuickCreateSalesSourceFromRow}
              onDeleteHierarchyRow={handleDeleteHierarchyRow}
              onAddHierarchyRow={viewMode === ViewMode.CHAIN_RETAILER_VIEW ? handleAddHierarchyRow : undefined}
              onAddSkuFromCatalog={viewMode === ViewMode.CHAIN_RETAILER_VIEW ? handleOpenAddSkuDialog : undefined}
              onEditLine={viewMode === ViewMode.CHAIN_RETAILER_VIEW ? handleOpenEditLine : undefined}
              onHierarchyRowNameChange={viewMode === ViewMode.CHAIN_RETAILER_VIEW ? handleHierarchyRowNameChange : undefined}
            />
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <ToolbarGroup label="Integrations">
              <div className="flex flex-wrap items-center gap-1.5">
                {sync_button.visible ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncActualsFromQuickBooks}
                    disabled={isPreviewingQuickBooks || !currentVersionId}
                    title={last_pull_sentence(sync_button) ?? undefined}
                  >
                    {isPreviewingQuickBooks ? "Syncing…" : sync_button.label}
                  </Button>
                ) : null}
                {compareCellsArePreview && compareCells.length > 0 ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleSaveActualsToForecast}
                    disabled={isSavingActuals || !currentVersionId}
                  >
                    {isSavingActuals ? "Saving…" : "Save to forecast"}
                  </Button>
                ) : null}
                {has_mapping_provider ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.location.assign("/sales-forecasting/mappings")}
                    title={
                      unmapped_mapping_count > 0
                        ? `${provider_label} lines that cannot land on this forecast until they are mapped`
                        : undefined
                    }
                  >
                    Manage mappings
                    {unmapped_mapping_count > 0 ? (
                      <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                        {unmapped_mapping_count}
                      </span>
                    ) : null}
                  </Button>
                ) : null}
                {compareCellsArePreview && compareCells.length > 0 && (
                  <span className="max-w-md text-xs leading-snug text-amber-800">
                    Review the compare grid (unsaved). Use <strong>Save to forecast</strong> when the numbers look
                    right — that writes {provider_label} actuals to this version.
                  </span>
                )}
                {actualizeError && (
                  <span className="text-xs text-red-600">{actualizeError}</span>
                )}
              </div>
            </ToolbarGroup>
            <ExportMenu
              onExport={exportData}
              shipWeekLeadTime={is_week_grain ? shipWeekLeadTime : null}
              onImport={currentVersionId ? () => setShowImportDialog(true) : undefined}
              onSeedFromActuals={currentVersionId ? () => setShowSeedDialog(true) : undefined}
            />
          </div>
        </>
      )}

      <ForecastImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        version_id={currentVersionId}
        version_name={versionManager.versions.find((v: ForecastVersion) => v.id === currentVersionId)?.name ?? null}
        on_applied={() => reloadAfterServerWrite()}
      />

      <SeedFromActualsDialog
        open={showSeedDialog}
        onOpenChange={setShowSeedDialog}
        target={versionManager.versions.find((v: ForecastVersion) => v.id === currentVersionId) ?? null}
        versions={versionManager.versions}
        on_applied={() => reloadAfterServerWrite()}
      />

      <Dialog open={showUnsavedReview} onOpenChange={setShowUnsavedReview}>
        <DialogContent className="max-w-lg gap-3">
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-600">Everything below differs from the saved version. Nothing is written until you Save.</p>
          {unsaved_diff ? <UnsavedChangesList diff={unsaved_diff} /> : null}
        </DialogContent>
      </Dialog>

      <ForecastSaveConfirmDialog
        open={saveConfirmOpen}
        onOpenChange={handle_save_confirm_dialog_open_change}
        is_saving={isSavingHierarchy}
        previous_totals={saveDialogBaseline}
        current_totals={save_confirm_current_totals}
        requires_approval={save_requires_approval(
          current_user,
          versionManager.versions.find((v: ForecastVersion) => v.id === pendingSaveVersionId),
          forecast_approvals_required
        )}
        unsaved_diff={unsaved_diff}
        onConfirm={(driver: string, notes: string) => void executeHierarchySaveAfterConfirm(driver, notes)}
      />

      <DiscardUnsavedChangesDialog
        open={pendingUnsavedAction != null}
        title={pendingUnsavedAction?.title ?? ""}
        description={pendingUnsavedAction?.description ?? ""}
        confirm_label={pendingUnsavedAction?.confirm_label ?? "Discard changes"}
        onOpenChange={(next: boolean) => {
          if (!next) {
            cancelPendingUnsavedAction()
          }
        }}
        onConfirm={confirmPendingUnsavedAction}
      />

      {/* Version Manager Dropdown */}
      <VersionManagerComponent
        versionManager={versionManager}
        currentData={data}
        currentPeriods={periods}
        compareMode={compareMode}
        comparisonPeriods={comparisonPeriods}
        onVersionChange={switchToVersion}
        onCreateBlankVersion={createBlankVersion}
        onVersionUpdate={updateVersion}
        onVersionDelete={deleteVersion}
        onVersionDuplicate={duplicateVersion}
        onVersionSetPrimary={setPrimaryVersion}
        onVersionSetLocked={setVersionLocked}
        onVersionSetArchived={setVersionArchived}
        onVersionForkCurrent={saveGridAsNewScenario}
        onVersionSave={requestSaveCurrentVersion}
        onVersionCompareStart={(version: ForecastVersion) => void startVersionCompare(version)}
        activeCompareName={versionCompare?.name ?? null}
        onVersionCompareStop={stopVersionCompare}
        isOpen={isVersionManagerOpen}
        onClose={() => setIsVersionManagerOpen(false)}
      />

      {/* Update forecast (top-down, sales source, AI links) */}
      <Dialog open={updateForecastDialogOpen} onOpenChange={setUpdateForecastDialogOpen}>
        <DialogContent className="max-w-md gap-4">
          <DialogHeader>
            <DialogTitle>Update forecast</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Scale remaining forecast to a revenue target, add a sales source, or ask the AI to propose changes.
            </p>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Top-down target</div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={topDownTarget}
                onInput={(event: Event) => {
                  setPendingTopDownCommit(null)
                  setTopDownTarget((event.target as HTMLInputElement).value)
                }}
                placeholder="Remaining forecast revenue"
                className="h-9 min-w-[10rem] flex-1"
              />
              <Button
                type="button"
                onClick={() => void applyTopDownTarget()}
                disabled={!currentVersionId || !topDownTarget.trim()}
                variant="outline"
                size="sm"
              >
                Preview target
              </Button>
            </div>
            {currentVersionId && top_down_period_caption ? (
              <p className="text-[11px] leading-snug text-gray-600">
                Replaces{" "}
                <strong className="text-gray-800">remaining forecast revenue</strong> — months still modeled as
                forecast — for <strong>{top_down_period_caption}</strong> only.{" "}
                <strong className="text-gray-800">Actualized</strong> months and <strong>locked</strong> rows are
                not scaled. Compare the <em>Remaining Forecast</em> card (plan columns{" "}
                {plan_column_range_caption ? <strong>{plan_column_range_caption}</strong> : null}).
                {hasData ? (
                  <>
                    {" "}
                    Current remaining (this view):{" "}
                    <strong className="tabular-nums text-gray-800">
                      {formatForecastCurrency(Number(displayForecastSummary?.forecast_volume_dollars ?? 0))}
                    </strong>
                    .
                  </>
                ) : null}
              </p>
            ) : null}
          </div>
          <div className="space-y-2 border-t border-gray-100 pt-4">
            <p className="text-xs font-medium text-gray-700">Other ways to update</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                setUpdateForecastDialogOpen(false)
                setSalesSourceDialogOpen(true)
              }}
            >
              Add sales source…
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              disabled={product_line_targets.length === 0}
              title={
                product_line_targets.length === 0
                  ? "No product-line rows yet — add a customer first."
                  : "Add a catalog product under an existing product line, with its UPC and case cost, so actuals can land on it."
              }
              onClick={() => {
                setUpdateForecastDialogOpen(false)
                setSkuTargetPickerOpen(true)
              }}
            >
              Add an item (SKU) under a product line…
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              disabled={isFillingCaseCosts}
              title="Set Case Cost from the catalog list price on SKU rows that have none. Never overwrites an existing cost and never changes revenue."
              onClick={() => void handleFillCaseCostsFromCatalog()}
            >
              {isFillingCaseCosts ? "Filling case costs…" : "Fill missing case costs from catalog"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-blue-700"
              onClick={() => {
                setUpdateForecastDialogOpen(false)
                setIsChatOpen(true)
              }}
            >
              Chat with ForecastWiz to adjust this forecast
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CreateSalesSourceDialog
        open={salesSourceDialogOpen}
        onOpenChange={setSalesSourceDialogOpen}
        hideTrigger
        onSave={handleCreateSalesSource}
        periods={sales_source_periods}
        existing_hierarchy_roots={data}
        triggerLabel="Add sales source"
      />

      <EnhancedPromotionEditModal
        promotion={openPromotion}
        isOpen={openPromotion != null}
        onClose={() => setOpenPromotion(null)}
        onSave={(promotion: Promotion) => setOpenPromotion(promotion)}
      />

      <SelectProductLineDialog
        open={skuTargetPickerOpen}
        targets={product_line_targets}
        onOpenChange={setSkuTargetPickerOpen}
        onSelect={(row_id: string) => {
          setSkuTargetPickerOpen(false)
          handleOpenAddSkuDialogForNodeId(row_id)
        }}
      />

      <EditLineDialog
        target={editLineTarget}
        periods={sales_source_periods}
        on_close={() => setEditLineTarget(null)}
        on_save={handleEditLineSave}
      />

      <AddSkuDialog
        target={addSkuTarget}
        periods={periods}
        default_start_month={forecastConfig.forecast_start_period || periods[0]?.period_key || ""}
        on_close={() => setAddSkuTarget(null)}
        on_add={handleAddSkuFromCatalog}
      />

      {/* Chat Panel */}
      <ChatPanel
        open={isChatOpen}
        onOpenChange={setIsChatOpen}
        forecastVersions={versionManager.versions}
        currentForecastVersionId={currentVersionId || undefined}
        onForecastChanged={() => {
          guardUnsavedChanges({
            title: "Discard unsaved forecast edits?",
            description:
              "The AI changed this forecast on the server. Loading those changes replaces the grid, so the cell edits you have not saved will be lost.",
            confirm_label: "Discard and load AI changes",
            run: async () => {
              if (currentVersionId) {
                await loadVersionData(currentVersionId)
              }
              await refreshForecastVersions()
            },
          })
        }}
      />

    </div>
  )
}

interface ForecastSummaryDetailLine {
  label: string
  value: string
}

interface ForecastSummaryCardProps {
  label: string
  value: string
  /** Short explanation under the headline number (readability for Actualized vs Remaining). */
  sub_label?: string
  detail_lines?: ForecastSummaryDetailLine[]
}

function opportunity_detail_lines_for_cards(
  lines: OpportunityKpiDetailLine[] | null
): ForecastSummaryDetailLine[] | undefined {
  if (!lines?.length) return undefined
  return lines.map((line: OpportunityKpiDetailLine) => ({
    label: line.title,
    value: formatForecastCurrency(line.amount),
  }))
}

function buildForecastSummaryCards(
  forecastSummary: ForecastAPI.ForecastSummaryResponse | null,
  opportunity_lines: OpportunityKpiDetailLine[] | null,
  plan_column_range_caption?: string | null,
  show_promo_lift_in_summary = false,
  gross_to_net?: GrossToNetBridge | null,
  trade_lift?: TradeLiftSummary | null,
  spans_years = false,
): ForecastSummaryCardProps[] {
  const actualVolumeDollars = Number(forecastSummary?.actual_volume_dollars || 0)
  const forecastVolumeDollars = Number(forecastSummary?.forecast_volume_dollars || 0)
  const totalAnnualForecastDollars = Number(
    forecastSummary?.total_volume_dollars ?? actualVolumeDollars + forecastVolumeDollars
  )
  const opp_details = opportunity_detail_lines_for_cards(opportunity_lines)
  const column_scope =
    plan_column_range_caption != null
      ? `Only months shown as columns (${plan_column_range_caption}) — sums match the table footer.`
      : "Sums match the table footer for the months shown in the grid."

  const promo_lift_total = Number(forecastSummary?.total_promo_lift_dollars || 0)
  const promo_units_total = Number(forecastSummary?.total_promo_lift_units || 0)
  const promo_detail_lines: ForecastSummaryDetailLine[] =
    show_promo_lift_in_summary && (promo_units_total > 0 || promo_lift_total > 0)
      ? [
          {
            label: "Promo lift (plan columns)",
            value: `${Math.round(promo_units_total).toLocaleString()} units · ${formatForecastCurrency(promo_lift_total)}`,
          },
        ]
      : []

  // Confido's own base/incremental split of the promoted periods. Shown as detail rather than
  // its own card because it is a *component* of the forecast, not another total to add to it.
  const trade_lift_lines: ForecastSummaryDetailLine[] =
    trade_lift && trade_lift.lift_revenue !== 0
      ? [
          { label: "Expected lift from trade", value: formatForecastCurrency(trade_lift.lift_revenue) },
          ...(trade_lift.roi != null
            ? [{ label: "Return on trade spend", value: `${trade_lift.roi.toFixed(2)}x` }]
            : []),
        ]
      : []

  const cards: ForecastSummaryCardProps[] = [
    {
      label: "Actualized",
      value: formatForecastCurrency(actualVolumeDollars),
      sub_label:
        "Revenue from months with actuals. If a leaf month has both an actual and a forecast row, only the actual counts (same rule as the grid).",
    },
    {
      label: "Remaining Forecast",
      value: formatForecastCurrency(forecastVolumeDollars),
      detail_lines: opp_details,
      sub_label:
        "Forecast and allocated months only — this is the bucket top-down scales. Does not include actualized months.",
    },
    {
      label: spans_years ? "Total Forecast" : "Total Annual Forecast",
      value: formatForecastCurrency(totalAnnualForecastDollars),
      detail_lines: [...promo_detail_lines, ...trade_lift_lines, ...(opp_details ?? [])],
      sub_label: `Actualized plus remaining. ${column_scope}`,
    },
  ]

  // A plan whose headline ignores a third of its own dollars reads gross-only; when the
  // version carries a trade spend plan, show what's left after it.
  if (gross_to_net && gross_to_net.spend !== 0) {
    cards.push({
      label: "Net Revenue",
      value: formatForecastCurrency(gross_to_net.revenue - gross_to_net.spend),
      detail_lines: [
        { label: "Trade spend", value: `−${formatForecastCurrency(gross_to_net.spend)}` },
        ...(gross_to_net.trade_rate_pct != null
          ? [{ label: "Trade rate", value: `${gross_to_net.trade_rate_pct.toFixed(1)}%` }]
          : []),
      ],
      sub_label: "Planned gross revenue less planned trade spend (see Gross to net below).",
    })
  }

  if (Number(forecastSummary?.total_cases_sold || 0) > 0) {
    cards.push({
      label: "Cases",
      value: format_cases(Number(forecastSummary?.total_cases_sold || 0)),
    })
  }

  if (forecastSummary?.top_down_variance != null && Number(forecastSummary.top_down_variance) !== 0) {
    cards.push({
      label: "Top-Down Variance",
      value: formatForecastCurrency(forecastSummary.top_down_variance),
    })
  }

  return cards
}

export interface ProductLineTarget {
  row_id: string
  path_caption: string
}

/**
 * Flatten the hierarchy to its product-line rows, captioned with their full path.
 *
 * A SKU has to hang off a product line, so the New menu needs somewhere to send the user; the
 * row-level "Add SKU from catalog" already knows its row, this is the same action reached from
 * the toolbar instead.
 */
export function collect_product_line_targets(roots: SalesHierarchyNode[]): ProductLineTarget[] {
  const targets: ProductLineTarget[] = []
  function walk(nodes: SalesHierarchyNode[], trail: string[]): void {
    for (const node of nodes) {
      const next_trail = [...trail, node.name]
      if (String(node.level) === HierarchyLevel.PRODUCT_LINE) {
        targets.push({ row_id: node.id, path_caption: next_trail.join(" → ") })
      }
      const children = (node as { children?: SalesHierarchyNode[] }).children
      if (Array.isArray(children)) walk(children, next_trail)
    }
  }
  walk(roots, [])
  targets.sort((a: ProductLineTarget, b: ProductLineTarget) => a.path_caption.localeCompare(b.path_caption))
  return targets
}

/** Pick the product line a new SKU goes under. Filters on the whole path, not just the leaf name. */
function SelectProductLineDialog(props: {
  open: boolean
  targets: ProductLineTarget[]
  onOpenChange: (open: boolean) => void
  onSelect: (row_id: string) => void
}) {
  const [query, setQuery] = useState("")
  const needle = query.trim().toLowerCase()
  const matches = needle
    ? props.targets.filter((target: ProductLineTarget) => target.path_caption.toLowerCase().includes(needle))
    : props.targets

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-xl gap-3">
        <DialogHeader>
          <DialogTitle>Where does this item go?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">
          Pick the product line the new SKU belongs under. You can also reach this from any product-line row's
          actions menu.
        </p>
        <Input
          value={query}
          placeholder="Filter by customer or product line…"
          onInput={(event: Event) => setQuery((event.target as HTMLInputElement).value)}
        />
        <div className="max-h-80 overflow-y-auto rounded-md border border-gray-200">
          {matches.length === 0 ? (
            <p className="px-3 py-4 text-sm text-gray-500">No product line matches that filter.</p>
          ) : (
            matches.map((target: ProductLineTarget) => (
              <button
                key={target.row_id}
                type="button"
                className="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-800 last:border-b-0 hover:bg-gray-50"
                onClick={() => props.onSelect(target.row_id)}
              >
                {target.path_caption}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface ToolbarGroupProps {
  label: string
  children: ComponentChildren
}

function ToolbarGroup({ label, children }: ToolbarGroupProps) {
  return (
    <section className="flex flex-wrap items-center gap-2 rounded-md bg-gray-50/70 px-2 py-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      {children}
    </section>
  )
}

interface OpportunityImpactPanelProps {
  opportunities: ForecastOpportunitySummary[]
  closed_opportunities: ForecastOpportunitySummary[]
  scenarioView: OpportunityScenarioView
  selectedOpportunityIds: Set<string>
  onViewInForecast: (opportunityId: string) => void
  onScenarioViewChange: (view: OpportunityScenarioView) => void
  onToggleOpportunity: (opportunityId: string) => void
  onLikelihoodChange: (opportunityId: string, likelihoodPct: number) => void
  onPromote: (opportunityId: string) => void
  onMarkLost: (opportunityId: string) => void
  onReopen: (opportunityId: string) => void
  onDelete: (opportunityId: string) => void
}

function OpportunityImpactPanel({
  opportunities,
  closed_opportunities,
  scenarioView,
  selectedOpportunityIds,
  onViewInForecast,
  onScenarioViewChange,
  onToggleOpportunity,
  onLikelihoodChange,
  onPromote,
  onMarkLost,
  onReopen,
  onDelete,
}: OpportunityImpactPanelProps) {
  const weightedTotal = opportunities.reduce((sum: number, opportunity: ForecastOpportunitySummary) => sum + opportunity.weighted_revenue, 0)
  const fullTotal = opportunities.reduce((sum: number, opportunity: ForecastOpportunitySummary) => sum + opportunity.full_revenue, 0)

  return (
    <section id="sales-opportunities-panel" className="scroll-mt-4 rounded-lg border border-amber-200 bg-white px-3 py-2 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">Sales Opportunities</h2>
          <p className="text-[11px] text-gray-600">
            Weighted upside {exactCurrencyFormatter(weightedTotal)} · Full upside {exactCurrencyFormatter(fullTotal)}
          </p>
          <p className="mt-0.5 max-w-3xl text-[11px] leading-snug text-gray-500">
            <span className="font-medium text-gray-700">Add to forecast</span> commits full upside as live rows and clears draft styling.
          </p>
        </div>
        <div className="flex shrink-0 rounded border border-gray-300 bg-gray-100 p-0.5">
          {([
            ["baseline", "Baseline"],
            ["weighted", "Weighted"],
            ["full_upside", "Full Upside"],
          ] as [OpportunityScenarioView, string][]).map(([value, label]) => (
            <Button
              key={value}
              variant="ghost"
              size="sm"
              onClick={() => onScenarioViewChange(value)}
              className={`h-7 border px-2 text-xs ${scenarioView === value ? 'border-amber-300 bg-amber-50 text-amber-900 shadow-sm hover:bg-amber-50' : 'border-transparent text-gray-600 hover:bg-gray-200'}`}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {opportunities.map((opportunity: ForecastOpportunitySummary) => {
          const isSelected = selectedOpportunityIds.size === 0 || selectedOpportunityIds.has(opportunity.id)
          return (
            <article
              key={opportunity.id}
              className={`rounded border p-2 ${isSelected ? 'border-gray-200 bg-gray-50' : 'border-gray-100 bg-white opacity-60'}`}
              style={{ borderLeft: `3px solid ${opportunity.color}` }}
            >
              <button
                type="button"
                onClick={() => onToggleOpportunity(opportunity.id)}
                className="w-full min-w-0 text-left"
              >
                <div className="truncate text-xs font-semibold text-gray-900">{opportunity.name}</div>
                {opportunity.hierarchy_path ? (
                  <p
                    className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-gray-500"
                    title={opportunity.hierarchy_path}
                  >
                    {opportunity.hierarchy_path}
                  </p>
                ) : null}
                {opportunity.row_count > 0 ? (
                  <p className="mt-0.5 text-[10px] text-gray-500">
                    {opportunity.row_count} leaf row{opportunity.row_count === 1 ? "" : "s"} in forecast
                  </p>
                ) : null}
                <div className="mt-0.5 text-[10px] leading-tight text-gray-600">
                  {/* Show the arithmetic, not just the endpoints, so the slider's effect is legible. */}
                  <span className="text-gray-600">{exactCurrencyFormatter(opportunity.full_revenue)} full</span>
                  {" "}× {Math.round(opportunity.likelihood_pct)}% ={" "}
                  <span className="font-medium text-gray-800">{exactCurrencyFormatter(opportunity.weighted_revenue)} wtd</span>
                </div>
              </button>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 h-7 w-full gap-1 px-2 text-xs text-gray-700 min-w-0"
                onClick={() => onViewInForecast(opportunity.id)}
                title="Scroll the forecast table to this opportunity's rows, expanding the hierarchy as needed"
              >
                <Crosshair className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">View in forecast</span>
              </Button>
              <div className="mt-1.5 flex flex-wrap gap-1">
                <Button
                  variant="default"
                  size="sm"
                  className="h-7 flex-1 gap-1 bg-emerald-700 px-2 text-xs text-white hover:bg-emerald-800 min-w-0"
                  onClick={() => onPromote(opportunity.id)}
                  title="Commit this opportunity as part of the baseline forecast at full value"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">Add</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 border-gray-300 px-2 text-xs text-gray-700 hover:bg-gray-100 min-w-0"
                  onClick={() => onMarkLost(opportunity.id)}
                  title="Record this deal as lost — its rows stay for win-rate history but stop counting anywhere"
                >
                  <XCircle className="h-3.5 w-3.5 shrink-0" />
                  <span className="hidden sm:inline truncate">Lost</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 border-red-200 px-2 text-xs text-red-700 hover:bg-red-50 min-w-0"
                  onClick={() => onDelete(opportunity.id)}
                  title="Remove this opportunity and its draft rows from the hierarchy entirely (no history kept)"
                >
                  <Trash2 className="h-3.5 w-3.5 shrink-0" />
                  <span className="hidden sm:inline truncate">Discard</span>
                </Button>
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={opportunity.likelihood_pct}
                  onChange={(event: Event) => onLikelihoodChange(opportunity.id, parseFloat((event.target as HTMLInputElement).value) || 0)}
                  className="h-7 w-16 shrink-0 text-xs [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <span className="shrink-0 text-[10px] font-medium text-gray-600">% likelihood</span>
              </div>
            </article>
          )
        })}
      </div>
      {closed_opportunities.length > 0 ? (
        <div className="mt-2 border-t border-amber-100 pt-2">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Closed</p>
          <div className="flex flex-wrap gap-1.5">
            {closed_opportunities.map((opportunity: ForecastOpportunitySummary) => (
              <div
                key={opportunity.id}
                className="flex items-center gap-1.5 rounded border border-gray-200 bg-gray-50 px-2 py-1"
              >
                <span
                  className={`text-[10px] font-semibold uppercase ${
                    opportunity.status === "live" ? "text-emerald-700" : "text-gray-500"
                  }`}
                >
                  {opportunity.status === "live" ? "Won" : "Lost"}
                </span>
                <span className="max-w-40 truncate text-xs text-gray-800" title={opportunity.name}>
                  {opportunity.name}
                </span>
                <span className="text-[10px] tabular-nums text-gray-600">
                  {exactCurrencyFormatter(opportunity.full_revenue)}
                </span>
                <button
                  type="button"
                  className="text-[10px] font-medium text-blue-700 hover:text-blue-900"
                  title="Move this deal back to the open pipeline at the likelihood it closed at"
                  onClick={() => onReopen(opportunity.id)}
                >
                  Reopen
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  )
}

const SHIP_TO_MONTHLY_EXPORT_KIND = "ship-to-revenue-by-month"
const SHIP_TO_SHIP_MONTH_EXPORT_KIND = "ship-to-revenue-by-ship-month"

interface ExportMenuProps {
  onExport: (kind: string) => void
  /** Weeks product ships ahead of consumption; null when the plan is not weekly. */
  shipWeekLeadTime: number | null
  /** Opens the CSV/XLSX import dialog; absent when no version is open. */
  onImport?: () => void
  /** Opens the "start from last year's actuals" dialog; absent when no version is open. */
  onSeedFromActuals?: () => void
}

function ExportMenu({ onExport, shipWeekLeadTime, onImport, onSeedFromActuals }: ExportMenuProps) {
  const exportKinds = [
    { id: "shipment", label: "Shipment Forecast" },
    { id: SHIP_TO_MONTHLY_EXPORT_KIND, label: "Ship-To Revenue by Month" },
    ...(shipWeekLeadTime != null
      ? [
          {
            id: SHIP_TO_SHIP_MONTH_EXPORT_KIND,
            label: `Ship-To Revenue by Ship Month (${shipWeekLeadTime} wk${shipWeekLeadTime === 1 ? "" : "s"} ahead)`,
          },
        ]
      : []),
    { id: "top-level", label: "Top Level" },
    { id: "line-item", label: "Line Item" },
    { id: "gross-to-net", label: "Gross to Net" },
  ]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {exportKinds.map((kind: { id: string; label: string }) => (
          <DropdownMenuItem key={kind.id} onClick={() => onExport(kind.id)}>
            {kind.label}
          </DropdownMenuItem>
        ))}
        {onImport || onSeedFromActuals ? <DropdownMenuSeparator /> : null}
        {onImport ? <DropdownMenuItem onClick={onImport}>Import from CSV / Excel…</DropdownMenuItem> : null}
        {onSeedFromActuals ? <DropdownMenuItem onClick={onSeedFromActuals}>Start from last year's actuals…</DropdownMenuItem> : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ForecastSummaryCard(props: ForecastSummaryCardProps) {
  const { label, value, sub_label, detail_lines } = props
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-gray-900">{value}</div>
      {sub_label ? (
        <p className="mt-2 text-[11px] leading-snug text-gray-600">{sub_label}</p>
      ) : null}
      {detail_lines?.length ? (
        <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-3">
          {detail_lines.map((line: ForecastSummaryDetailLine, idx: number) => (
            <div key={`${line.label}-${idx}`} className="flex justify-between gap-3 text-[11px] leading-snug">
              <span className="min-w-0 flex-1 text-gray-500">{line.label}</span>
              <span className="shrink-0 tabular-nums font-medium text-gray-800">{line.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function formatForecastCurrency(value?: string | number): string {
  const numericValue = Number(value || 0)
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(numericValue) ? numericValue : 0)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("buildForecastSummaryCards", () => {
    it("shows actualized, remaining forecast, and total annual forecast in order", () => {
      const cards = buildForecastSummaryCards(
        {
          version_id: "version-1",
          total_volume_dollars: "300",
          forecast_volume_dollars: "200",
          actual_volume_dollars: "100",
          total_cases_sold: 0,
          total_store_count: 0,
        },
        null,
        null
      )

      expect(cards.map((card: ForecastSummaryCardProps) => card.label)).toEqual([
        "Actualized",
        "Remaining Forecast",
        "Total Annual Forecast",
      ])
      expect(cards[0].value).toBe("$100")
      expect(cards[1].value).toBe("$200")
      expect(cards[2].value).toBe("$300")
    })

    it("renames the total card when the visible columns span calendar years", () => {
      const cards = buildForecastSummaryCards(
        {
          version_id: "version-1",
          total_volume_dollars: "300",
          forecast_volume_dollars: "200",
          actual_volume_dollars: "100",
          total_cases_sold: 0,
          total_store_count: 0,
        },
        null,
        "2026-W37–2027-W52",
        false,
        null,
        null,
        true,
      )
      expect(cards[2].label).toBe("Total Forecast")
    })

    it("only shows cases and top-down variance when populated", () => {
      const cards = buildForecastSummaryCards(
        {
          version_id: "version-1",
          total_volume_dollars: "300",
          forecast_volume_dollars: "200",
          actual_volume_dollars: "100",
          total_cases_sold: 12,
          total_store_count: 0,
          top_down_variance: "50",
        },
        null,
        null
      )

      expect(cards.map((card: ForecastSummaryCardProps) => card.label)).toEqual([
        "Actualized",
        "Remaining Forecast",
        "Total Annual Forecast",
        "Cases",
        "Top-Down Variance",
      ])
    })

    it("adds matching opportunity detail lines on forecast cards", () => {
      const cards = buildForecastSummaryCards(
        {
          version_id: "version-1",
          total_volume_dollars: "300",
          forecast_volume_dollars: "200",
          actual_volume_dollars: "100",
          total_cases_sold: 0,
          total_store_count: 0,
        },
        [{ title: "Opportunities (weighted)", amount: 42 }],
        null
      )
      expect(cards[1].detail_lines?.[0]?.label).toBe("Opportunities (weighted)")
      expect(cards[1].detail_lines?.[0]?.value).toBe("$42")
      const total_opp = cards[2].detail_lines?.find((l) => l.label === "Opportunities (weighted)")
      expect(total_opp?.value).toBe("$42")
      expect(cards[0].detail_lines).toBeUndefined()
    })

    it("adds promo lift units and dollars on Total Annual Forecast when toolbar on and data present", () => {
      const cards = buildForecastSummaryCards(
        {
          version_id: "version-1",
          total_volume_dollars: "300",
          forecast_volume_dollars: "200",
          actual_volume_dollars: "100",
          total_cases_sold: 0,
          total_store_count: 0,
          total_promo_lift_dollars: 1_250,
          total_promo_lift_units: 400,
        },
        null,
        null,
        true
      )
      const promo_line = cards[2].detail_lines?.find((l) => l.label === "Promo lift (plan columns)")
      expect(promo_line?.value).toBe("400 units · $1,250")
    })

    it("surfaces expected lift from trade spend on Total Annual Forecast", () => {
      const cards = buildForecastSummaryCards(
        {
          version_id: "version-1",
          total_volume_dollars: "300",
          forecast_volume_dollars: "200",
          actual_volume_dollars: "100",
          total_cases_sold: 0,
          total_store_count: 0,
        },
        null,
        null,
        false,
        null,
        {
          spend: 1000,
          base_revenue: 8000,
          lift_revenue: 2500,
          incremental_revenue: 2000,
          overlapping_revenue: 500,
          promoted_revenue: 10500,
          lift_units: 2500,
          roi: 2.5,
          promoted_trade_rate_pct: 9.5,
          lift_share_pct: 23.8,
          periods: [],
          tactics: [],
          customers: [],
          top_events: [],
          remote_promo_ids: [],
          event_count: 3,
        }
      )
      const lift_line = cards[2].detail_lines?.find((l) => l.label === "Expected lift from trade")
      expect(lift_line?.value).toBe("$2,500")
      const roi_line = cards[2].detail_lines?.find((l) => l.label === "Return on trade spend")
      expect(roi_line?.value).toBe("2.50x")
    })

    it("omits the lift lines when the version carries no trade calendar", () => {
      const cards = buildForecastSummaryCards(
        {
          version_id: "version-1",
          total_volume_dollars: "300",
          forecast_volume_dollars: "200",
          actual_volume_dollars: "100",
          total_cases_sold: 0,
          total_store_count: 0,
        },
        null,
        null,
        false,
        null,
        null
      )
      expect(cards[2].detail_lines?.some((l) => l.label === "Expected lift from trade")).toBe(false)
    })
  })

  describe("collect_product_line_targets", () => {
    const hierarchy = [
      {
        id: "cot-1",
        name: "Natural",
        level: "class_of_trade",
        children: [
          {
            id: "cust-1",
            name: "Sprouts",
            level: "customer",
            children: [
              { id: "pl-2", name: "Sparkling Water - SS", level: "product_line", children: [] },
              { id: "pl-1", name: "Sparkling Water - MP", level: "product_line", children: [{ id: "sku-1", name: "Yuzu", level: "sku" }] },
            ],
          },
        ],
      },
    ] as unknown as SalesHierarchyNode[]

    it("captions each product line with its full path, sorted", () => {
      expect(collect_product_line_targets(hierarchy)).toEqual([
        { row_id: "pl-1", path_caption: "Natural → Sprouts → Sparkling Water - MP" },
        { row_id: "pl-2", path_caption: "Natural → Sprouts → Sparkling Water - SS" },
      ])
    })

    it("returns nothing for a hierarchy with no product lines, which disables the menu entry", () => {
      expect(collect_product_line_targets([{ id: "c", name: "Sprouts", level: "customer" } as any])).toEqual([])
    })
  })
}
